import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
/* Map 图标必须起别名（MapIcon）：lucide 的 Map 会遮蔽 JS 内置 Map 构造函数，
   本文件里有 `new Map()`（附件去重索引），会变成 `new <React组件>` 并抛
   "default is not a constructor"，整个阶段会话抽屉直接白屏。
   （2026-10-22 实测：点矩阵阶段格打开会话即报此错。） */
import { ChevronDown, ChevronRight, CheckCircle2, Download, FileText, FolderOpen, GitBranch, Hourglass, Landmark, Loader2, Map as MapIcon, MessageSquareText, Package, RotateCcw, Settings2, Trash2, UploadCloud, X, XCircle } from "lucide-react";
import { useApp } from "../../../context/AppContext";
import { toast } from "../../../components/ui/sonner";
import { T, TE } from "../../../lib/i18n";
import { authStore } from "../../../lib/api";
import { fmtT, doneDownstreamOf, flowFrontierOf, flowOrderOf, stageTooEarlyOf } from "./meta";
import { showContractWarnings } from "../../../lib/contractWarn";
import TaskFilesModal from "../TaskFilesModal";
import { MediaLightbox, mediaKind } from "../../../components/MediaLightbox";
import { downloadFile } from "../../../lib/fileDownload";
import { uploadChunked, uploadErrText, UPLOAD_MAX, appendUploadRef } from "../../../lib/upload";
import { Comments } from "./Comments";
import { CityReviewBlock } from "./CityReviewBlock";
import { LandSurveyBlock } from "./LandSurveyBlock";
import FileUploadDate, { uploadDateLabel } from "./FileUploadDate";
import { FILE_TYPE_HINT, projectOwnerOptions, ownerOptionLabel, DESIGN_UPLOAD_ACCEPT, sortFileVersions, useDiscAgencyMap, reverseAgencyMap, AG_DONE } from "./meta";// 阶段会话标题沿用详情阶段条的简称，避免从列表点进来后标题又变回模板全名。
import { STAGE_SHORT, FILE_TYPE_ZH, TASK_TO_DISC, LAND_SURVEY_STAGES, CITY_REVIEW_STAGES, inferFileType, AgencyRow, stageMergeGroupOf, stageMemberKeys } from "./LegacyTabs";
import { mergePbDrawingRequirements, mergePbCopies, mergedPbStatus, pbMissingKinds, pbTaskFiles, uploadPbDrawings } from "./pbDrawings";

const STATUS_LABEL = {
  "Not Started": "未开始",
  Ready: "就绪",
  "In Progress": "进行中",
  "Internal Review": "内部审核",
  Submitted: "已提交",
  "Awaiting Client": "等客户",
  "Awaiting External": "等外部",
  "Correction Required": "需整改",
  Blocked: "受阻",
  Approved: "已批准",
  Completed: "已完成",
  "Not Applicable": "不适用",
};

/* 可回退的阶段状态集合（2026-10-23 新增「回退阶段」）。
   与后端 design_module.stage_reopen_core 的校验完全同口径：只有已经「有结论」的阶段
   才谈得上退回 —— Completed / Approved 是正常完成，Correction Required 是整改中但不该
   卡在原地，Reopened 是之前被退过一次又关上。进行中的阶段不显示回退（退了也没变化）。 */
const REOPENABLE_STATUSES = ["Completed", "Approved", "Correction Required", "Reopened"];

const statusClass = (status) => {
  if (["Completed", "Approved", "Not Applicable"].includes(status)) return "bg-emerald-100 text-emerald-700";
  if (["Blocked", "Correction Required"].includes(status)) return "bg-red-100 text-red-700";
  if (["Awaiting Client", "Awaiting External", "Submitted"].includes(status)) return "bg-amber-100 text-amber-700";
  if (["In Progress", "Internal Review"].includes(status)) return "bg-sky-100 text-sky-700";
  if (status === "Ready") return "bg-indigo-100 text-indigo-700";
  return "bg-slate-100 text-slate-500";
};

/* 任务侧上传版本的三个业务状态（2026-10-21）。
   任务文件只有 Draft / Submitted / Approved / Superseded 四档，没有产物侧那套
   复核记录（pending / accepted / needs_fix），所以两边的状态徽标各用一套映射；
   但文案与配色刻意对齐产物侧，避免同一交付在任务行里说「已批准」、在产物卡里说
   「审核通过」被读成两件不同的事。 */
const TASK_FILE_STATE = {
  Draft: { label: "工作稿", cls: "bg-sky-100 text-sky-700" },
  Submitted: { label: "已提交", cls: "bg-amber-100 text-amber-800" },
  Approved: { label: "审核通过", cls: "bg-emerald-100 text-emerald-700" },
  Superseded: { label: "已被新版替代", cls: "bg-slate-200 text-slate-600" },
};

// 与详情任务卡「快速上传」共用同一组可接受格式（2026-10-20：收敛到 meta 的共享常量，
// 原来这里另写一份，导致 PPT / CDR 等新类型只改一处时两个入口又会不一致）。
const TASK_ACCEPT = DESIGN_UPLOAD_ACCEPT;

/**
 * 阶段聊天抽屉。
 *
 * 2026-10-19 需求：矩阵保持原来的列表/格子样式，但格子从产物改为阶段；
 * 点击阶段进入聊天窗口，右侧按详情同一份节点数据展示该阶段要求的产物。
 * 聊天本身复用项目评论接口并固定 stageKey，因此窗口内的消息、回复、附件和待办
 * 都写入详情可见的 design_v3_comments / 任务数据，不另建一套不可追溯的聊天记录。
 *
 * 2026-10-19 二次需求（用户反馈）：
 *   ① 右侧不能只是「需 811 Ticket」这样的只读文字，否则从列表快捷进来后没有任何
 *      操作入口，还得再绕回项目详情；
 *   ② 811 Ticket 这类英文文件类型看不懂。
 * 因此这里改为复用详情阶段任务的同一份数据（/design/projects/{pid}），在右侧直接
 * 提供：任务状态 / 负责人 / 快速上传 / 文件与版本（TaskFilesModal），并把文件类型
 * 显示成中文 + 一句业务解释。交互口径与详情阶段卡保持一致，两个入口看到的是同一份
 * design_tasks 数据，不会出现会话里传完详情看不到的情况。
 */
const StageChatDrawer = ({ pid, stageKey, meta, onClose, onChanged, onOpenProject }) => {
  const { api } = useApp();
  const [project, setProject] = useState(null);
  const [nodes, setNodes] = useState(null);
  // 详情接口（legacy）里才有任务的 files / requiredFileTypes / primaryOwner 全量字段；
  // 节点接口只给节点检查用的精简信息，二者按 taskId 对齐。
  const [legacy, setLegacy] = useState(null);
  const [filesTaskId, setFilesTaskId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [err, setErr] = useState("");
  /* 右侧「阶段所需产物」批量操作（2026-10-19 用户需求）：
     · batchOpen：展开多选清单；默认全部勾选 = 都要提交；
     · draftKeep：弹窗内暂存的勾选（保存前不落库）；
     · showSkipped：下方「无需提交」折叠区是否展开。 */
  const [batchOpen, setBatchOpen] = useState(false);
  const [draftKeep, setDraftKeep] = useState([]);
  const [showSkipped, setShowSkipped] = useState(true);
  const [savingSkip, setSavingSkip] = useState(false);
  // 产物「提交 / 审核」正在处理的那一项（按 discKey），用于按钮防重与局部 loading。
  const [busyProduct, setBusyProduct] = useState(null);
  /* 驳回后重传（2026-10-19 用户需求）。
     用户描述的闭环是「审核不通过 → 提交人上传第二版 → 再审核」，所以阶段会话里
     必须能直接传新版本，否则驳回之后提交人还得跳回项目详情找产物卡片。
     这里用隐藏 input + 当前产物 key 实现「点一下选文件即上传并提交」。 */
  const [uploadFor, setUploadFor] = useState(null);
  const uploadRef = useRef(null);
  /* 产物人员配置（2026-10-19 用户需求）：设计经理在阶段会话里就地指定
     「谁来交、谁来审」，不必再跳回项目详情。同一时刻只展开一项。
     下拉初值取**显式配置值**（d.submitterUsername），不是有效值 —— 有效值可能是
     由项目设计师推导出来的，直接回填会在只想改审核人时把提交人也固化下来。 */
  const [partyEdit, setPartyEdit] = useState(null);
  const [partySub, setPartySub] = useState("");
  const [partyRev, setPartyRev] = useState("");
  const [savingParty, setSavingParty] = useState(false);
  // 完成阶段（2026-10-21 恢复）：门禁条旁边给经理一个入口，
  // 未通过时后端返回 blocking 明细，可填原因强关（与详情页阶段卡同一套后端规则）。
  const [closingStage, setClosingStage] = useState(false);
  /* 版本附件预览（2026-10-20 用户反馈「点击文件后系统打开新标签栏什么也不显示」）。
     版本行文件名原本是 `<a target="_blank" href="/api/files/{id}/download">`：
     后端该接口带 `Content-Disposition: inline`，浏览器对 zip / CAD / 大 PDF 无法内嵌渲染，
     于是先下载、再留下一个空白标签页。现在改为与「文件与版本」弹窗同一套页内灯箱预览，
     只有灯箱里的下载按钮才真正落盘（走 blob，不新开标签）。
     这里存的是「当前产物 key + 该产物的预览索引」，切换产物时索引不会串。 */
  const [previewVersion, setPreviewVersion] = useState(null);
  /* 任务要求行的版本灯箱（2026-10-21 多版本平铺）：存「当前任务 id + 该行清单里的索引」。
     独立于 previewVersion：产物卡与任务行是两份清单，共用一个索引会串位。 */
  const [previewSubmission, setPreviewSubmission] = useState(null);

  const loadProject = useCallback(async () => {
    try {
      const { data } = await api.get(`/design/v3/projects/${pid}`);
      setProject(data);
      setErr("");
    } catch (e) {
      setErr(e?.detailText || e.message);
    }
  }, [api, pid]);

  const loadNodes = useCallback(async () => {
    try {
      const { data } = await api.get(`/design/v3/projects/${pid}/nodes`);
      setNodes(data.stages || []);
    } catch {
      // 节点导航是阶段产物的只读索引；失败时仍保留聊天和项目详情数据。
      setNodes([]);
    }
  }, [api, pid]);

  const loadLegacy = useCallback(async () => {
    try {
      const { data } = await api.get(`/design/projects/${pid}`);
      setLegacy(data);
    } catch {
      // 拿不到完整任务数据时，右侧仍展示节点接口里的「所需文件」，只是不提供编辑操作。
      setLegacy({ stages: [] });
    }
  }, [api, pid]);

  useEffect(() => {
    setProject(null);
    setNodes(null);
    setLegacy(null);
    setFilesTaskId(null);
    loadProject();
    loadNodes();
    loadLegacy();
  }, [loadProject, loadNodes, loadLegacy]);

  /* 【2026-10-22 P+B 合并】抽屉也要按「一个节点」打开。
     用户口径：「不是照旧..是在 p+b 模式中的节点中铺开...东西都要...只是吧之前的 15 个阶段
     改变..了可能就是换换了一套流程」。矩阵点合并格传下来的是 pb_xxx；详情深链 / 旧收藏
     仍可能传单个阶段 key（planning_review），在 concurrent 项目里也必须落到整组，
     否则同一件事从两个入口进来看到的是两种形态（一个节点 vs 半个节点）。

     mode 取项目文档的 approvalMode：项目接口（/design/v3/projects/{pid}）与详情接口
     （/design/projects/{pid}）都下发该字段，先取前者（加载更早），再兜底后者。 */
  const mode = (project?.approvalMode || legacy?.project?.approvalMode || "sequential") === "concurrent" ? "concurrent" : "sequential";
  /* 固定合并（结构/T24 → Building 图）与 P+B 并发合并共用这套入口：
     新项目只有 building_arch；存量项目还会返回 structural_t24，但两者都落到同一个
     显示节点，右侧任务 / 产物 / 机构必须一次铺开，不能因为旧深链只显示半套。 */
  const memberKeys = useMemo(() => stageMemberKeys(stageKey, mode), [stageKey, mode]);
  const group = stageMergeGroupOf(stageKey, mode);
  const groupEntry = memberKeys.length > 1;
  const displayKey = groupEntry && group ? group.key : stageKey;
  /* 组成员节点（按组内书写顺序，第一个是主阶段）。nodes 还没回来时为空数组。 */
  const memberNodes = useMemo(() => {
    const byKey = Object.fromEntries((nodes || []).map((n) => [n.key, n]));
    return memberKeys.map((k) => byKey[k]).filter(Boolean);
  }, [nodes, memberKeys]);
  /* 新项目的 building_arch 本身已含结构 / T24，库里不会再建 structural_t24；
     这时仍按成员清单去取数据，但是否显示成合并节点必须看实际存在的节点。 */
  const merged = memberNodes.length > 1;
  const stage = memberNodes[0] || null;
  const stageName = (merged && group ? group.name : "") || STAGE_SHORT[displayKey] || stage?.name || meta?.stageTemplate?.find((s) => s.key === displayKey)?.name || displayKey;
  /* 2026-10-19 用户需求：右侧只展示当前流程（阶段）需要的产物。
     nodes 接口按阶段返回 disciplines（_nc_linked 只取该阶段挂载的产物），
     因此这里只取与 stageKey 匹配的节点，不再把所有阶段串在一起展示。

     【2026-10-22 P+B 合并】合并节点 = 组内全部阶段的产物 / 任务要求 / 机构**平铺**成一份：
     用户在同一个节点里看到的就是这一轮要交的全部东西，不再分成两套「Planning 的」「Building 的」。
     产物 key 全局唯一（13 项），不需要按阶段去重；机构与任务行按 id / taskId 去重（防同一家
     机构被两个阶段重复挂载）。 */
  const stageNode = useMemo(() => {
    /* 【2026-10-23 修复】非合并阶段也要有 stageIds。
       修改原因：加「回退阶段」时发现，stageIds 原本只在下面的合并分支里算，普通阶段
       （方案设计 / 现场测量这类不在 P+B 组里的）走到这里直接 return stage —— 而
       nodes 里的阶段对象**没有** stageIds 字段，于是抽屉里回退按钮的显示条件
       （stageNode.stageIds?.some(...)）恒为 false，按钮根本不出现。
       合并节点当时能用、单阶段不能用，正是因为这个分支差异（实测踩到）。
       最终效果：合并节点 = 组内全部成员；普通阶段 = 它自己一条，两种形态同构，
       下面的「完成 / 回退」两处按钮不必再各写一套取值逻辑。 */
    if (!merged) {
      if (!stage) return null;
      return { ...stage, stageIds: [{ id: stage.id, key: stage.key, done: stage.done, status: stage.status }] };
    }
    if (!memberNodes.length) return null;
    const discs = memberNodes.flatMap((n) => n.disciplines || []);
    const ags = [];
    const agSeen = new Set();
    memberNodes.forEach((n) => (n.agencies || []).forEach((a) => {
      if (agSeen.has(a.id)) return;
      agSeen.add(a.id);
      ags.push(a);
    }));
    const reqs = [];
    const reqSeen = new Set();
    memberNodes.forEach((n) => (n.requiredFiles || []).forEach((r) => {
      if (reqSeen.has(r.taskId)) return;
      reqSeen.add(r.taskId);
      reqs.push(r);
    }));
    /* 聊天锁取组内**最宽松**的一把：只要组里有一个阶段允许发言，这个合并节点就能聊 ——
       组成员本来就是同时推进的（后端 _stage_chat_lock 已按 CONCURRENT_STAGE_GROUPS 同层放行，
       两把锁理论上同时开；这里做兜底，避免任一侧数据缺失把整个节点锁死）。 */
    const locks = memberNodes.map((n) => n.chatLock || { allowed: true });
    const openLock = locks.find((l) => l.allowed !== false) || locks[0] || { allowed: true };
    const gateDiscs = discs.filter((x) => !x.skipped);
    const blocking = gateDiscs.filter((x) => !x.approved);
    return {
      key: displayKey,
      name: group?.name || stageName,
      /* done / current / productGate 都按整组聚合：
         · done = 组内全部阶段都完成（节点才是完成态）；
         · current = 任一是当前阶段（节点标「当前」）；
         · productGate = 全组产物合并计数（门禁条说「还差几项」，不能只算一半）。 */
      done: memberNodes.every((n) => n.done),
      current: memberNodes.some((n) => n.current),
      disciplines: discs,
      agencies: ags,
      requiredFiles: reqs,
      chatLock: openLock,
      productGate: {
        total: gateDiscs.length,
        approved: gateDiscs.filter((x) => x.approved).length,
        ok: !blocking.length,
        blocking: blocking.map((x) => ({ key: x.key, name: x.name, zh: x.zh, status: x.status, label: x.label, reviewPending: !!x.reviewPending })),
      },
      /* 阶段 id 清单：抽屉里的「完成该节点」要按组顺序逐个关阶段（每个阶段有自己的门禁），
         「回退该节点」要挑出其中已完成的那几个 —— 两个动作都靠这份清单，避免渲染层再去
         nodes 里翻。status 一并带上：回退按钮的显示条件与后端 stage_reopen 的可回退集合
         判定都靠它（2026-10-23 新增回退功能时补的字段）。 */
      stageIds: memberNodes.map((n) => ({ id: n.id, key: n.key, done: n.done, status: n.status })),
    };
  }, [merged, stage, memberNodes, displayKey, group, stageName]);
  const stageProducts = stageNode?.disciplines || [];
  /* 本节点是否「审图节点」：合并模式下看组里有没有审图阶段（pb_review 含两个）；
     单阶段模式下就是 planning_review / building_review 本身。
     用途：审图节点的完成入口是 City 块的「已通过」，不能再挂一个绕过附件校验的关阶段按钮。 */
  const reviewNode = merged
    ? memberKeys.some((k) => CITY_REVIEW_STAGES.includes(k))
    : CITY_REVIEW_STAGES.includes(stageKey);
  /* 批量「无需提交」的产物（2026-10-19 用户需求）：
     被取消勾选的产物不删数据、不改全局阶段挂载，只在本项目的 stageSkip 里记一笔，
     状态置 exempt（既有终态，不影响阶段完成度与推进）。右侧主列表只显示要交的，
     跳过的收进下方折叠区，可随时恢复。 */
  const activeProducts = stageProducts.filter((x) => !x.skipped);
  const skippedProducts = stageProducts.filter((x) => x.skipped);
  const chatLock = stageNode?.chatLock || { allowed: true };
  /* 流程顺序门禁：没轮到的阶段不能先点「完成该阶段」。与后端 _stage_order_lock /
     stage_close_core 同一份判定 —— 基准是**流程前沿**（第一个未收口阶段），
     不再是 currentStage 指针。nodes 接口里的 stage 节点不带 order，所以从 legacy
     阶段链取（它带 order）。

     【2026-10-23 基准修正，用户「不能跳流程让阶段开始....」】
     上一版拿 `project.currentStage` 的 order 比大小，可指针自己会跳站：City「一次通过」
     连关两个审图阶段、补关历史阶段、回退后重新推进，都会让指针跑在实际进度前面。
     开发库实测 107 个在途项目里 11 个指针在前沿之后（planning_review 仍 Ready、
     指针已到 building_review）—— 这些项目「前面没做、后面却能点完成」。
     改按流程前沿判定后，前沿自己永远放行，「当前阶段能操作」不受影响。

     【P+B 合并】并发项目的显示 key（pb_xxx）会经 flowOrderOf 映射到五节点固定层序；
     原始 order 只作为顺序项目 / 数据异常时的兜底。

     【2026-10-24 与显示同源】顺序项目也把阶段 key 投影到「当前模板层号」再比：
     存量项目库内 order 是建项当天的旧顺序（Building 材料在提交前），2026-10-23
     模板已把材料挪到审查后 —— 不投影的话，会话抽屉里「完成该阶段」的置灰口径
     会和详情页矩阵显示的顺序打架（线上 3236 Rockhold 实测）。模板 key 优先取
     meta.stageTemplate（运行时权威），缺省时 flowOrderOf/flowFrontierOf 用内置镜像。 */
  const stageTooEarly = useMemo(() => {
    const raw = legacy?.stages || [];
    if (!raw.length) return null;
    const mine = memberKeys.map((k) => raw.find((x) => x.key === k)).filter(Boolean);
    if (!mine.length) return null;
    const orders = mine.map((x) => x.order).filter((o) => o != null);
    if (!orders.length) return null;
    const tplKeys = meta?.stageTemplate?.map((x) => x.key) || null;
    const frontier = flowFrontierOf(raw, legacy?.project, tplKeys);
    return stageTooEarlyOf(flowOrderOf(displayKey, Math.min(...orders), legacy?.project, tplKeys), frontier);
  }, [legacy, memberKeys, displayKey, meta]);
  /* 详情阶段卡除了「关联产物」，还会列出各子任务要求的文件（如现场测量图/照片、811 Ticket）。
     右侧同步展示这部分要求，未开始阶段即使没有关联产物，也能看到本阶段需要交什么。 */
  const stageRequirements = useMemo(
    () => mergePbDrawingRequirements(stageNode?.requiredFiles || [], mode, displayKey),
    [stageNode, mode, displayKey],
  );
  /* 机构对接（2026-10-22 用户需求「主要就是和现在的详情中的阶段对齐」）。
     差异：详情阶段卡有「机构对接 / 其他机构对接」，会话右侧此前完全不渲染机构 ——
     开发库实测 planning_paperwork 阶段详情有 2 家机构、会话右侧一片空白，
     structural_t24 / building_paperwork 同样缺这 10 家。
     为什么必须补：机构（消防局 / 学区 / 排污 / AQMD …）是这一阶段真实要办的事，
     用户从矩阵点进来只看到产物和任务，会以为本阶段没有报批动作。
     数据来源与详情一致：nodes 里的 stageNode.agencies（按阶段挂载）+ legacy.agencyDocs
     （每个机构类型要求的清单项），复用同一个 AgencyRow 组件，口径不会两处漂。
     与详情页码的对齐口径：
       · 挂在产物下的机构 → 已经在产物卡里（详情同样如此，这里不重复列）；
       · 没有对应产物的机构 → 「其他机构对接」区块（与详情同一个标题与口径）。 */
  const stageAgencies = stageNode?.agencies || [];
  const agencyDocs = legacy?.agencyDocs || {};
  /* 机构 → 产物 的反向表：与详情阶段卡同一份配置（「产物时间设置 → 产物↔机构」）。
     用来判断某家机构是否已经挂在产物卡页脚里，避免同一家在两处各列一遍。 */
  const agMap = useDiscAgencyMap(api);
  const agToDisc = useMemo(() => reverseAgencyMap(agMap), [agMap]);
  const designers = meta?.designers || [];
  /* 去重方向（2026-10-21 用户两次澄清后的最终口径）：
     用户先反馈「详情和类别矩阵聊天框右侧都重复了」，随后明确「应该保留上面那种…
     你保留反了」「两个边都是」「Planning Submission Set 这个阶段也是保留上面的」
     「之前卡片本来就有这个功能啊 上传 → 送审 → 通过」。

     所以保留的是**任务要求行**（它带负责人 / 上传 / 文件与版本 / 状态），
     收起的是与之配对的**产物卡**（产物卡只有状态 + 文件数两样信息）。
     配对关系与详情阶段卡共用 TASK_TO_DISC（改一处要两边一起改）。

     边界：只收起「本阶段确实有对应任务要求行」的产物；其余产物（人工办理 / 机构类，
     没有任务行可承载）照常显示 —— 否则它们在这条侧栏里会彻底消失。
     注意：只隐藏渲染，产物数据不动 —— 阶段门禁 / 审核记录仍按 disciplines 判断。 */
  const productKeySet = useMemo(() => new Set((stageNode?.disciplines || []).map((x) => x.key)), [stageNode]);
  const requirementDiscKeys = useMemo(() => new Set(stageRequirements.flatMap((r) => r.discKeys || [TASK_TO_DISC[r.title]]).filter(Boolean)), [stageRequirements]);
  /* 被任务要求行承载的产物 key：产物在本阶段挂载 + 本阶段有同名配对任务要求行。 */
  const carriedProductKeys = useMemo(() => {
    const out = new Set();
    requirementDiscKeys.forEach((dk) => {
      if (productKeySet.has(dk)) out.add(dk);
    });
    return out;
  }, [requirementDiscKeys, productKeySet]);

  /* 批量设置的**作用域**：只包含「没有任务要求行承载、需要在这一栏单独决定交不交」的产物。
     2026-10-22 恢复（与详情阶段卡同口径）：配对产物由任务行承载，不进这个弹窗 ——
     否则同一件事在弹窗里又列一遍，还能被勾成「无需提交」，与它由任务卡承载的事实矛盾。 */
  const batchScopeProducts = useMemo(
    () => stageProducts.filter((x) => !carriedProductKeys.has(x.key)),
    [stageProducts, carriedProductKeys],
  );
  /* 主列表实际画出来的产物数：与渲染口径一致（active 且未被任务行承载）。
     2026-10-22 恢复「配对只留一个」的口径（见下方渲染处注释）：配对产物由任务行承载，
     这里必须扣掉，否则「N 项」写着 1、下面只有任务行没有产物卡，数字对不上。 */
  const visibleProducts = useMemo(
    () => activeProducts.filter((x) => !carriedProductKeys.has(x.key)),
    [activeProducts, carriedProductKeys],
  );

  // taskId → 详情任务对象：上传、状态、负责人、文件版本都基于这一份数据操作。
  const tasksById = useMemo(() => {
    const map = {};
    (legacy?.stages || []).forEach((s) => (s.tasks || []).forEach((t) => {
      if (t?.id) map[t.id] = t;
    }));
    return map;
  }, [legacy]);

  /* 任务操作后同时刷新节点和详情任务：节点决定右侧状态徽标，详情决定文件 / 负责人；
     onChanged 让背后的矩阵列表和项目详情同步刷新，避免关掉会话后还看到旧状态。 */
  const reloadStage = () => {
    loadNodes();
    loadLegacy();
    onChanged?.();
  };

  /* 打开批量弹窗时用当前「要提交」的产物做初始勾选（默认全选）。
     已跳过的产物不参与勾选，它们由下方折叠区单独「恢复」；
     被任务卡承载的产物也不进清单（见 batchScopeProducts 注释）。 */
  const openBatch = () => {
    setDraftKeep(batchScopeProducts.filter((x) => !x.skipped).map((x) => x.key));
    setBatchOpen(true);
  };
  const toggleDraft = (key) => setDraftKeep((v) => (v.includes(key) ? v.filter((x) => x !== key) : [...v, key]));
  /* 产物「无需提交」的写入封装（2026-10-22 P+B 合并）。
     后端 product-skip 是**按阶段**的：它只在该阶段挂载的产物里反推「谁被取消勾选」，
     传进来的其它阶段产物会被忽略。所以合并节点必须对组内每个阶段各发一次，
     否则「在合并节点里取消勾选 Building 的产物」会静默无效（接口 200，数据没变）。
     每次传的都是**全量保留清单**（两边的产物都在里面），各阶段各自过滤即可。 */
  const postSkip = async (keep) => {
    const keys = Array.from(new Set(keep));
    const targets = merged ? memberKeys : [stageKey];
    for (const k of targets) {
      await api.post(`/design/v3/projects/${pid}/stages/${k}/product-skip`, { keys });
    }
  };
  /* 保存批量结果：后端按「保留清单」反推无需提交的产物，
     把它们置 exempt + 记录 stageSkip；重新勾选的则恢复原状态。
     ⚠️ 保留清单必须补上被任务卡承载的产物（carriedProductKeys）：
     后端是以「本次传的 keys」为唯一保留口径反推 skip 的，少传一个就会被置成
     exempt —— 那等于用批量弹窗把任务卡正在办的 Floor Plan 标成「无需提交」，
     正是用户反馈的这个重复/矛盾点。弹窗里看不到它们，但保存时必须替它们投保留票。 */
  const saveBatch = async () => {
    setSavingSkip(true);
    try {
      const keep = Array.from(new Set([...draftKeep, ...carriedProductKeys]));
      const before = (stageNode?.disciplines || []).filter((x) => x.skipped).length;
      await postSkip(keep);
      /* 提示口径按「本次前后差」算，而不是只看最后一次请求的返回：
         合并节点会连发两次，最后一次的 newlySkipped 只代表其中一个阶段。 */
      reloadStage();
      const afterKeys = new Set(keep);
      const willSkip = (stageNode?.disciplines || []).filter((x) => !x.skipped && !afterKeys.has(x.key) && !carriedProductKeys.has(x.key)).length;
      if (willSkip || before) toast.success(T("已更新：无需提交 {0} 项，恢复提交 {1} 项", [willSkip, before]));
      else toast.message(T("未变更"));
      setBatchOpen(false);
      setShowSkipped(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSavingSkip(false);
    }
  };
  /* 折叠区单项恢复：把该产物重新加入「要提交」清单后保存。
     同样要带上被任务卡承载的产物，理由同上（否则恢复一项、误跳过若干项）。 */
  const restoreProduct = async (key) => {
    const keep = Array.from(new Set([...activeProducts.map((x) => x.key), ...carriedProductKeys, key]));
    setSavingSkip(true);
    try {
      await postSkip(keep);
      toast.success(T("已恢复为需要提交"));
      reloadStage();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSavingSkip(false);
    }
  };

  /* 产物「提交」（2026-10-19 用户需求）。
     走专用 submit 接口而不是 PATCH 状态：后端会校验操作者是否该产物配置的提交人，
     并在同一次调用里完成「状态 → 已提交 + 建审核单 + 通知审核人」，
     避免前端只改状态、漏建复核单导致产物交了却没人审。
     若该产物在全局配置里是「无需审核」，后端会在同一次调用里直接置为完成，
     这里按返回的 reviewRequired 区分提示，不能一律说「等待 XX 审核」。 */
  const submitProduct = async (item) => {
    setBusyProduct(item.key);
    try {
      const { data } = await api.post(`/design/v3/projects/${pid}/disciplines/${item.key}/submit`, {});
      toast.success(data?.reviewRequired === false
        ? T("「{0}」已提交并完成（该产物无需审核）", [T(item.name)])
        : T("「{0}」已提交，等待 {1} 审核", [T(item.name), (data?.parties?.reviewer || {}).name || T("设计部经理")]));
      (data?.automation || []).forEach((a) => toast.message(T("自动化 {0}", [String(a.rule || "").toUpperCase()]), { description: a.text }));
      reloadStage();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusyProduct(null);
    }
  };

  /* 驳回后上传新版并重新送审（2026-10-19 用户需求）。
     用户要的闭环是「审核不通过 → 提交人传第二版 → 再审核」，这一步必须能在阶段会话里完成：
       · 产物在「整改中 / 制作中」时上传，后端 upload 接口会先把状态推进到「已提交」，
         再由 _set_disc 的 submitted 钩子按**这一版**建复核单并通知审核人；
       · 产物已是「已提交 / 审图中」时（待审期间补传新版），upload 会把待复核记录原地
         挂到新版本上，旧版标记为「已被新版替代」，不重复通知、不重置计时。
     因此前端只发一次上传请求即可，不需要再调 submit —— 那一步在状态已是已提交时是空操作。 */
  const pickVersion = (item) => {
    setUploadFor(item);
    uploadRef.current?.click();
  };
  const uploadVersion = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    const item = uploadFor;
    setUploadFor(null);
    if (!f || !item) return;
    if (f.size > UPLOAD_MAX) return toast.error(T("文件不能超过 200MB"));
    setBusyProduct(item.key);
    try {
      /* 【2026-10-22】改走**分片上传**（Xiyuan 07:07：「同事需要传 cad，大概 100-200M」）。
         原来这里只做 60MB 直传，整套 CAD 根本传不上来。
         分片通道全站早就有了（2MB/片、断点续传、SHA-256 校验、上限 200MB），
         只是设计部这个入口没接。现在：文件先分片进存储拿 fileId，
         再带 fileId + 元信息登记成产物版本 —— 服务端不经手字节。
         `shrink: false`：保持和原来的直传一致，不对图片做压缩改写。 */
      const up = await uploadChunked(api, f, { name: f.name, type: f.type, shrink: false });
      const fd = new FormData();
      fd.append("fileId", up.fileId);
      fd.append("fileName", up.filename || f.name);
      fd.append("fileSize", String(up.size ?? f.size));
      fd.append("contentType", up.contentType || f.type || "application/octet-stream");
      fd.append("markSubmitted", "1");
      fd.append("note", "");
      const { data } = await api.post(`/design/v3/projects/${pid}/disciplines/${item.key}/files`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      // 免审核产物已完成时是「换一版」而不是「重新送审」（2026-10-21）：
      // 它本来就没有审核环节，提示里说「送审」会让人以为要等谁批。
      const noReviewReplace = item.approved && item.reviewRequired === false;
      /* 越级上传（2026-10-23 用户需求「阶段限制时后面的阶段依然可以让先将资料产物等先上传上去...
         但是不会改变阶段显示的状态」）：后端现在放行，但只存档、不改任何状态。
         提示必须跟着改口径 —— 否则用户看到「已上传并重新送审」会以为流程被推进了，
         回头发现阶段没动又要问一遍「为什么没反应」。这里明确说「未送审、流程走到时再提交」。 */
      toast.success(data?.deferred
        ? T("「{0}」已存档 v{1}（流程还没走到本阶段，状态未变，走到后再提交审核）", [T(item.name), data?.file?.version ?? ""])
        : (noReviewReplace
          ? T("「{0}」已换上 v{1}（该产物无需审核，直接生效）", [T(item.name), data?.file?.version ?? ""])
          : T("「{0}」已上传 v{1} 并重新送审", [T(item.name), data?.file?.version ?? ""])));
      (data?.automation || []).forEach((a) => toast.message(T("自动化 {0}", [String(a.rule || "").toUpperCase()]), { description: a.text }));
      reloadStage();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setBusyProduct(null);
    }
  };

  /* 产物「审核」（2026-10-19 用户需求）：通过 → 产物自动完成；退回必须填原因且自动派返工。
     复用既有的复核决定接口，退回与产物会话的「退回重做」是同一套副作用。 */
  const decideProduct = async (item, result) => {
    let comment = "";
    if (result === "needs_fix") {
      comment = (window.prompt(T("请填写退回原因（将通知提交人并派修改任务）")) || "").trim();
      if (!comment) return;
    }
    setBusyProduct(item.key);
    try {
      // 阶段节点里的产物只带审核状态，不带复核记录 id；按项目取一次待复核列表再按产物匹配。
      const { data: rv } = await api.get(`/design/v3/projects/${pid}`);
      const rec = (rv?.reviews || []).find((r) => r.disciplineKey === item.key && r.status === "pending");
      if (!rec) {
        toast.error(T("没有找到该产物的待审核记录，请刷新后重试"));
        return;
      }
      const { data } = await api.post(`/design/v3/reviews/${rec.id}/decide`, { result, comment });
      toast.success(result === "accepted"
        ? T("审核通过，「{0}」已完成", [T(item.name)])
        : T("已退回，「{0}」进入整改", [T(item.name)]));
      (data?.automation || []).forEach((a) => toast.message(T("自动化 {0}", [String(a.rule || "").toUpperCase()]), { description: a.text }));
      reloadStage();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusyProduct(null);
    }
  };

  const decidePbDrawings = async (items, result) => {
    let comment = "";
    if (result === "needs_fix") {
      comment = (window.prompt(T("请填写 P+B drawings 退回原因（将通知提交人并派修改任务）")) || "").trim();
      if (!comment) return;
    }
    setBusyProduct("pb-drawings");
    try {
      const { data: rv } = await api.get(`/design/v3/projects/${pid}`);
      for (const item of items) {
        const rec = (rv?.reviews || []).find((review) => review.disciplineKey === item.key && review.status === "pending");
        if (!rec) throw new Error(T("没有找到 {0} 的待审核记录，请刷新后重试", [T(item.name)]));
        await api.post(`/design/v3/reviews/${rec.id}/decide`, { result, comment });
      }
      toast.success(result === "accepted" ? T("P+B drawings 审核通过") : T("P+B drawings 已退回整改"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusyProduct(null);
      reloadStage();
    }
  };

  /* 产物人员配置保存（2026-10-19 用户需求）：调用后端 parties 接口，
     后端会校验账号有效性、提交人≠审核人，并把该产物上挂着的待复核记录同步给新审核人。 */
  const saveParty = async (item) => {
    setSavingParty(true);
    try {
      await api.put(`/design/v3/projects/${pid}/disciplines/${item.key}/parties`, {
        submitterUsername: partySub || "",
        reviewerUsername: partyRev || "",
      });
      toast.success(T("已更新「{0}」的提交人 / 审核人", [T(item.name)]));
      setPartyEdit(null);
      reloadStage();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSavingParty(false);
    }
  };

  /* 完成阶段（2026-10-21 恢复，用户补充需求；2026-10-22 自动推进停用 + 加顺序门禁）。
     背景：2026-10-21 曾按「所有资料齐全时自动进行到下一步」删掉这个函数，推进改由后端
     maybe_auto_advance_stage 自动触发。用户随后明确「这个每个阶段的关闭按钮还是需要，
     这个关闭阶段其实就是完成阶段的意思」—— 他要的是「人工确认本阶段做完」这个动作本身。
     2026-10-22 用户再次确认「我已经将完成该阶段的按钮加回来了..那之前的任务提交就完成的
     自动应去掉了」：自动推进已停用（design_module._AUTO_ADVANCE_ENABLED = False），
     完成阶段只走这个人点按钮的入口。
     同轮还要求「整个流程现在需要按整个流程推进」—— 没轮到的阶段不能先完成，
     按钮按 order 与 currentStage 比较置灰；后端 stage_close_core 同口径兜底。
     门禁口径与详情页阶段卡完全一致（产物门禁 / 缺件门禁，经理可填原因强关并留痕）。 */
  const closeStage = async (override, overrideReason) => {
    /* 【2026-10-22 P+B 合并】合并节点 = 一次关掉组内**全部未完成**阶段。
       用户口径：「在 p+b 模式中的节点中铺开...东西都要」—— 这个节点既然是「一个图、
       一套材料」，完成动作也必须是节点级的：逐个关会让用户点两次、中间还夹着一个
       「另一个阶段还亮着」的半完成态。已完成的阶段跳过（幂等，重复点不会 400）。
       顺序按 CONCURRENT_GROUPS 的书写顺序（与后端 correction_pass 一致），
       这样 currentStage 的推进轨迹与 City 通过时完全一样。 */
    const targets = merged
      ? (stageNode?.stageIds || []).filter((x) => !x.done)
      : (stageNode?.id ? [{ id: stageNode.id, key: stageNode.key }] : []);
    if (!targets.length) return;
    setClosingStage(true);
    try {
      for (const t of targets) {
        await api.post(`/design/stages/${t.id}/close`,
          override ? { override: true, overrideReason: overrideReason || "" } : {});
      }
      toast.success(merged
        ? T("节点已完成（{0} 个阶段一起关闭），下一节点激活", [targets.length])
        : T("阶段已完成，下一阶段激活"));
      reloadStage();
    } catch (e) {
      const det = e?.response?.data?.detailRaw ?? e?.response?.data?.detail;
      if (det?.error === "stage_order_violation") {
        // 流程顺序门禁：越级完成没有 override 通道 —— 跳过前面阶段会让整条流程错位，
        // 不是「本单不做某项」这类可以用理由覆盖的例外。
        toast.error(det.message || T("流程还没走到本阶段，请先完成当前阶段"));
      } else if (det?.error === "stage_products_not_approved") {
        const list = (det.detail || []).map((x) => `${x.name}（${x.label}）${x.reviewPending ? ` · ${T("待审核")}` : ""}${x.reviewReason ? ` · ${T(x.reviewReason)}` : ""}`).join("\n");
        if (meta?.isManager && window.confirm(`${det.message || T("本阶段还有产物未完成审核。")}\n\n${list}\n\n${T("是否填写原因，强行完成本阶段？")}`)) {
          const reason = (window.prompt(T("请填写强行完成阶段的原因（至少 4 个字，会记录在项目动态里）"), "") || "").trim();
          if (reason.length < 4) return toast.error(T("必须填写原因（至少 4 个字）"));
          return closeStage(true, reason);
        }
        toast.error(T("还有 {0} 项产物未通过审核：{1}", [String((det.detail || []).length), (det.detail || []).map((x) => x.name).join("、")]));
      } else if (det?.error === "gate_files_missing") {
        const msg = (det.detail || []).map((x) => `${x.task}: ${x.missing.join("、")}`).join("\n");
        toast.error(T("缺少必需文件：") + msg);
      } else if (det?.error === "override_reason_required") {
        toast.error(det.message || T("必须填写强行完成阶段的原因"));
      } else {
        toast.error(typeof det === "string" ? det : e.message);
      }
    } finally {
      setClosingStage(false);
    }
  };

  /* 回退阶段（2026-10-23 用户需求）。
     需求原话：「这个项目需要回退到方案设计阶段，505 也是」（矩阵截图，红框在已完成的
     「方案设计」格上）。用户是在**矩阵**里点的格子 → 打开的就是本抽屉，所以这个入口
     必须和详情页阶段卡一样有，否则用户从最常用的入口进来根本退不了。

     与 closeStage 的关系：那边是「未完成 → 已完成」，这边是「已完成 → 进行中」，
     两者作用在同一批阶段上、互斥显示，所以放在一起、共用 stageIds 清单。

     交互沿用详情页同一套（确认 → 填原因 → 选是否连带下游），三个系统的破坏性操作
     都用 window.confirm/prompt，避免同一件事在两个页面长得不一样。 */
  const reopenStage = async () => {
    const targets = (stageNode?.stageIds || []).filter((x) => REOPENABLE_STATUSES.includes(x.status));
    if (!targets.length) { toast.error(T("该阶段还没完成，无需回退")); return; }
    const label = merged
      ? T("节点「{0}」内的 {1} 个已完成阶段", [stageNode?.name || displayKey, targets.length])
      : T("阶段「{0}」", [stageNode?.name || stageName]);
    if (!window.confirm(T("把{0}退回「进行中」？\n\n本阶段的产物会退回「制作中」，但已上传的图纸、附件与版本历史全部保留，重新提交时版本号自动递增为 v2。", [label]))) return;
    const reason = (window.prompt(T("请填写回退原因（至少 4 个字，会记录在项目动态里）"), "") || "").trim();
    if (reason.length < 4) { toast.error(T("必须填写原因（至少 4 个字）")); return; }
    /* 下游已完成阶段（2026-10-23 补）：用户实测反馈「回退了 7712，但中间的阶段还是显示已完成」。
       原实现只退下游**未完成**阶段，已完成的下游被留在原地，矩阵就成了
       「进行中 → 已完成 → 未开始」的矛盾样子，而且系统没给任何提示。
       现在先把这份名单摆出来让人决定：一起退（cascade="all"）还是留着。

       名单用 legacy.stages（= /design/projects/{pid} 的完整阶段链）而不是 nodes：
       nodes 是节点索引，不下发 actualFinish，弹窗里就显示不出「哪天完成的」，
       而完成日期正是让用户判断「这份图到底该不该重做」的关键信息。
       两者 key / order 一致，取 order 的口径相同。

       【2026-10-24 与显示同源】「下游」改按当前模板层号算（doneDownstreamOf 内部
       投影），与后端 stage_reopen 的 cascade 下游集合同口径 —— 存量项目旧 order 里
       paperwork 排在 submit/review 之前，按旧 order 找下游会漏掉屏幕上显示为本阶段
       下游的 paperwork，弹窗与实际回退范围对不上。 */
    const chain = (legacy?.stages && legacy.stages.length ? legacy.stages : nodes) || [];
    const doneDown = doneDownstreamOf(chain, (stageNode?.stageIds || []).map((x) => x.key).filter(Boolean),
                                       meta?.stageTemplate?.map((x) => x.key) || null);
    let cascade = false;
    if (doneDown.length) {
      const list = doneDown.slice(0, 8).map((x) => `· ${T(x.name)}${x.actualFinish ? `（${fmtT(x.actualFinish)} ${T("已完成")}）` : ""}`).join("\n");
      const more = doneDown.length > 8 ? `\n… ${T("另有 {0} 个", [doneDown.length - 8])}` : "";
      cascade = window.confirm(T("这个阶段之后还有 {0} 个阶段已经完成了：\n\n{1}{2}\n\n要一起退回「未开始」吗？\n\n确定 = 连同这些已完成阶段一起退（整条流程从本阶段重来）\n取消 = 只退本阶段，这些已完成阶段保持不动（矩阵里会看到中间夹着「已完成」）", [doneDown.length, list, more])) ? "all" : false;
    } else {
      cascade = window.confirm(T("是否连同本阶段之后、尚未完成的阶段一起退回「未开始」？\n\n确定 = 一起退回；取消 = 只回退本阶段，下游保持不动。")) ? true : false;
    }
    setClosingStage(true);
    try {
      for (const t of targets) await api.post(`/design/stages/${t.id}/reopen`, { reason, cascade });
      toast.success(merged
        ? T("节点已回退（{0} 个阶段），可以从本阶段重新开始", [targets.length])
        : (cascade === "all" && doneDown.length
          ? T("阶段已回退，{0} 个下游阶段也一起退回未开始", [doneDown.length])
          : T("阶段已回退，可以从这里重新开始")));
      reloadStage();
    } catch (e) {
      const det = e?.response?.data?.detailRaw ?? e?.response?.data?.detail;
      toast.error(typeof det === "string" ? det : (det?.message || e.message));
    } finally {
      setClosingStage(false);
    }
  };

  const patchTask = async (task, patch, override) => {
    try {
      await api.patch(`/design/tasks/${task.id}`, { ...patch, ...(override ? { override: true } : {}) });
      toast.success(T("已更新"));
      reloadStage();
    } catch (e) {
      const det = e?.response?.data?.detailRaw ?? e?.response?.data?.detail;
      if (det?.error === "gate_files_missing") {
        // 与详情任务卡相同：缺少必需文件时，经理可显式 Override 强制完成。
        if (meta?.isManager && window.confirm(T("缺少必需文件：{0}。是否经理 Override 强制完成？", [det.missing.join("、")]))) {
          return patchTask(task, patch, true);
        }
        toast.error(T("缺少必需文件：") + det.missing.join("、"));
      } else if (det?.error === "contract_file_missing") {
        if (meta?.isManager && window.confirm(`${det.message || T("「合同签署」还没有真实合同附件。")}\n\n${T("是否经理 Override 强制完成？")}`)) {
          return patchTask(task, patch, true);
        }
        toast.error(det.message || T("请先上传签约合同 PDF，再标记「合同签署」完成。"));
      } else {
        toast.error(typeof det === "string" ? det : e.message);
      }
    }
  };

  const patchPbOwners = async (tasks, owner) => {
    try {
      for (const task of tasks) {
        await api.patch(`/design/tasks/${task.id}`, { primaryOwner: owner || null });
      }
      toast.success(T("P+B drawings 负责人已更新"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      reloadStage();
    }
  };

  /* 快速上传：沿用详情的类型推断（PDF / CAD / 照片 / 其他），并逐个上传。
     多个文件时把已推断的类型累加进 have，避免同一批文件全部落到第一个缺口类型。

     越级上传（2026-10-23 用户需求「阶段限制时后面的阶段依然可以让先将资料产物等先上传上去...
     但是不会改变阶段显示的状态」）：后端已改为放行但只存档。这里按返回的 deferred 给出
     不同提示 —— 用户必须知道「传是传上去了，但这一步还没轮到，状态没变」，
     否则会以为上传失败或流程没反应。 */
  const uploadFiles = async (task, fileList, pairedTasks = null) => {
    const arr = Array.from(fileList || []);
    if (!arr.length) return;
    setUploadingId(task.id);
    if (pairedTasks) {
      try {
        const results = await uploadPbDrawings(api, arr, pairedTasks);
        results.forEach((data) => showContractWarnings(data?.warnings));
        toast.success(T("已上传 {0} 份 P+B drawings，Planning 与 Building 共用同一次提交", [arr.length]));
        if (results.some((data) => data?.deferred)) toast.message(T("文件已存档，流程走到本阶段后再送审"));
      } catch (e) {
        toast.error(T("P+B drawings 上传未全部完成，请查看版本记录后重试：{0}", [uploadErrText(e, true)]));
      } finally {
        setUploadingId(null);
        reloadStage();
      }
      return;
    }
    const have = (task.files || []).map((f) => f.type);
    let deferred = false;
    /* 越级上传会把**任务卡自己的状态**推进到「进行中」（后端 task_upload），
       但阶段 / 流程状态一律不动。提示要把这两件事分开说，否则用户会以为
       「说没变、可卡片明明变了」。 */
    let started = false;
    try {
      for (const f of arr) {
        const type = inferFileType(f.name, task.requiredFileTypes || [], have);
        have.push(type);
        const up = await uploadChunked(api, f, { shrink: false });
        const fd = new FormData();
        appendUploadRef(fd, up, f);
        fd.append("type", type);
        fd.append("status", "Draft");
        const { data } = await api.post(`/design/tasks/${task.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (data?.deferred) deferred = true;
        if (data?.taskStarted) started = true;
        // 合同槽位的门牌软校验（只提示不拦截），与详情 / 文件弹窗保持同一口径。
        showContractWarnings(data?.warnings);
      }
      toast.success(deferred
        ? (started
          ? T("已存档 {0} 个文件到「{1}」· 任务已开始，阶段流程还没走到", [arr.length, T(task.title)])
          : T("已存档 {0} 个文件到「{1}」（流程还没走到本阶段，阶段状态未变）", [arr.length, T(task.title)]))
        : T("已上传 {0} 个文件到「{1}」", [arr.length, T(task.title)]));
      reloadStage();
    } catch (e) {
      toast.error(uploadErrText(e, true));
    } finally {
      setUploadingId(null);
    }
  };

  const filesRequirement = stageRequirements.find((r) => r.pbDrawings && r.taskId === filesTaskId);
  const pairedFilesTasks = filesRequirement ? filesRequirement.taskIds.map((id) => tasksById[id]).filter(Boolean) : null;
  const filesTask = pairedFilesTasks?.length === 2
    ? { ...pairedFilesTasks[0], title: filesRequirement.title, files: pbTaskFiles(pairedFilesTasks), requiredFileTypes: ["P+B PDF", "P+B CAD"] }
    : filesTaskId ? tasksById[filesTaskId] : null;

  /* 版本作废（2026-10-21 用户需求）。
     用户原话：「上传错误的文件要怎么删除……包括那种不需要审核的上传完后对应的负责人也能去修改」。
     这里补的正是这条链路：
       · 谁能作废 = 上传人本人 / 该产物配置的提交人 / 审核人 / 设计主管
         （后端 `_void_disc_file` 同一判定，前端先按同一口径决定是否渲染按钮，
          不让用户点下去才被 403）；
       · 「无需审核」的产物没有审核人，但提交人一般就是这条产物的负责人，
         所以配置成免审核时依然有人能处理错版 —— 这是本次要解决的核心场景；
       · 只作废不删除：fileId / blob / 作废原因全部留档，符合「设计图纸历史可追溯」红线。
     版本行已经显示 v1 / v2、提交人、审核人，作废后该行保留并加删除线（见 v.voided 分支）。 */
  const [voidingVersion, setVoidingVersion] = useState(null);
  const myUsername = meta?.me?.username;
  const canVoidVersion = (item, v) => {
    if (!meta?.canEdit || !v || v.voided) return false;
    if (meta?.isManager) return true;
    const parties = item.parties || {};
    return v.submitterUsername === myUsername
      || v.uploaderUsername === myUsername
      || (parties.submitter || {}).username === myUsername
      || (parties.reviewer || {}).username === myUsername;
  };
  const voidVersion = async (item, v) => {
    const why = (window.prompt(T("作废「{0}」v{1} 的原因（不会物理删除，记录仍留档）", [v.name, v.version ?? ""]), "") || "").trim();
    if (!why) return;
    if (why.length < 4) return toast.error(T("请填写作废原因（至少 4 个字）"));
    setVoidingVersion(v.aid);
    try {
      await api.post(`/design/v3/projects/${pid}/disciplines/${item.key}/files/${v.aid}/void`, { reason: why });
      toast.success(T("已作废 v{0}，历史仍留档", [v.version ?? ""]));
      reloadStage();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setVoidingVersion(null);
    }
  };

  /* 产物版本 → 灯箱条目（2026-10-20 修「点文件打开空白标签」）。
     与 TaskFilesModal 的 lbItems 同口径：只收录有 fileId 的版本，带上媒体类型与展示元信息。
     MediaLightbox 拿到 api + fileId 后，PDF 会走后端逐页渲染（大文件不再黑屏），
     图片 / 视频 / 表格就地预览，其余类型给出明确提示 + 下载按钮。
     排序沿用与详情页一致的 sortFileVersions：审批通过的版本在前，其余按 v1/v2 正序，
     保证灯箱里「上一张 / 下一张」的顺序与屏幕上看到的版本顺序完全一致。 */
  const versionLbItems = (product) => sortFileVersions((product && product.versions) || [])
    .filter((v) => v.fileId)
    .map((v) => ({
      id: v.aid || `${product.key}-${v.version}-${v.at}`,
      fileId: v.fileId,
      size: v.size,
      url: `${process.env.REACT_APP_BACKEND_URL}/api/files/${v.fileId}/download?auth=${authStore.get("ali_token") || ""}`,
      // contentType 让 Office / 音频能按 MIME 兜底识别（扩展名缺失时仍可判断）。
      kind: mediaKind(v.name, v.contentType || ""),
      name: `${v.version == null ? T("参考") : `v${v.version}`} · ${v.name}${v.voided ? T("（已作废）") : ""}`,
      meta: [v.size ? `${Math.max(1, Math.round(v.size / 1024))}KB` : "", v.submitter || "", uploadDateLabel(v.uploadedAt, v.at)].filter(Boolean).join(" · "),
    }));
  const previewProduct = previewVersion ? stageProducts.find((x) => x.key === previewVersion.key) : null;
  const previewItems = previewProduct ? versionLbItems(previewProduct) : [];
  /* 点版本文件名 / 预览图标：在该产物自己的版本清单里定位索引（列表已按版本倒序，
     与界面上看到的顺序一致），直接开页内灯箱。 */
  const openVersionPreview = (item, v) => {
    const items = versionLbItems(item);
    const idx = items.findIndex((x) => x.fileId === v.fileId);
    if (idx < 0) return;
    setPreviewVersion({ key: item.key, index: idx });
  };

  /* ===== 任务行承载的提交物：多版本平铺（2026-10-21 用户需求） =====
     用户原话：「右侧的产物或者提交物都会有那种多版本的情况，但是现在有的需要点击它
     才显示在一个按钮上（1/2/3 个历史），有的是将产物全部排列出来，我需要的是产物都
     排列出来那种方式」。

     为什么任务行此前只剩一个数字：配对产物（floor_plan / planning / building /
     structural / t_24）的产物卡已按 2026-10-21 的口径收进「已在上面任务内办理」摘要行，
     版本清单随之一并消失；任务行自己又只在「文件与版本 N」按钮上显示计数。于是同一份
     交付的多轮历史只剩一个数字，想看第 2 版必须先点开弹窗 —— 正是用户说的「藏在一个
     按钮上」。

     这里把两侧数据合成一份**平铺清单**（不再折叠、不再只给计数）：
       · 任务侧 design_tasks.files —— 设计师在任务卡上传的版本；
       · 产物侧 disciplines[].versions —— 在产物侧上传并走审核的版本（带复核结论）。
     去重只按 fileId：开发库实测同一份 PDF 在两侧各有一份记录（fileId 不同），说明那是
     两次真实上传，都要保留；fileId 相同则说明是同一份文件，只留一条并标「任务+产物」，
     不重复计数。
     版本号来自两套独立序列（任务按文件类型计、产物按产物计），所以徽标只写 vN，
     不把两个数字拼成一条序列，避免被读成「任务侧第 3 版 = 产物侧第 3 版」。 */

  /** 产物侧版本状态：与产物卡内联清单同一份口径（原先两处各写一套映射，改一处会漂移）。 */
  const discVersionState = (v) => {
    const st = (v.review || {}).status;
    const phase = v.phase || (v.review ? "reviewed" : "historical");
    if (v.voided) return { label: "已作废", cls: "bg-rose-100 text-rose-700" };
    if (st === "accepted") return { label: "审核通过", cls: "bg-emerald-100 text-emerald-700" };
    if (st === "needs_fix") return { label: "退回修改", cls: "bg-red-100 text-red-700" };
    if (st === "pending") return { label: "待审核", cls: "bg-amber-100 text-amber-800" };
    if (st === "superseded") {
      /* 作废有两种来源，必须分开显示（2026-10-23 用户需求「回退后需要重新审核」）：
         · 被回退作废：流程被退回重做，这一版永远不会再被审，用户必须重新提交；
         · 被新版替代：正常的迭代（待审期间又传了新版），审核对象换成了新版本。
         两种都显示「已被新版替代」会让用户误以为有人接手审了，其实要自己重交。 */
      return v.review?.supersededBy === "stage_reopen"
        ? { label: "因回退作废", cls: "bg-rose-100 text-rose-700" }
        : { label: "已被新版替代", cls: "bg-slate-200 text-slate-600" };
    }
    if (phase === "rework") return { label: "整改返工稿", cls: "bg-orange-100 text-orange-700" };
    if (phase === "draft") return { label: "工作稿", cls: "bg-sky-100 text-sky-700" };
    return { label: "已归档", cls: "bg-slate-100 text-slate-500" };
  };

  /** 一条任务要求行下要平铺的全部提交版本（任务侧 + 配对产物侧，按 fileId 去重）。
   *  排序：作废件沉底 → 审核通过的在前 → 其余按上传时间正序（「第一次、第二次提交」）。 */
  const submissionRowsOf = (task, pairDisc) => {
    const rows = [];
    const seen = new Map();
    const push = (row) => {
      const key = row.fileId || row.aid;
      if (key && seen.has(key)) {
        /* 同一份文件两侧都有：合进已有那条，不再多一行。
           2026-10-22 补：合并时必须把产物侧的审核信息补进来。
           背景：任务卡上传的图纸现在会自动归集为产物版本（同一 fileId），
           于是这个「两侧都有」的分支从「历史巧合」变成**每次上传都会走到**。
           原实现只标 both=true 就返回，而任务侧是先 push 的 —— 结果产物侧那条
           携带的 review / reviewer / state 全被丢掉，任务行永远显示「工作稿」，
           用户看不到「这一版审核通过 / 待审核」。 */
        const prev = seen.get(key);
        prev.both = true;
        if (row.source === "disc") {
          // 审核结论、审核人、产物侧状态以产物记录为准（那才是走流程的那一份）。
          prev.review = row.review || prev.review;
          prev.reviewer = row.reviewer || prev.reviewer;
          prev.state = row.state || prev.state;
          prev.source = "task+disc";
          // 作废标记取「任一为真」：任务侧删了或产物侧作废了，这一版都不该再算有效。
          prev.voided = !!(prev.voided || row.voided);
        }
        return;
      }
      if (key) seen.set(key, row);
      rows.push(row);
    };
    (task?.files || []).forEach((f) => {
      const st = TASK_FILE_STATE[f.status] || TASK_FILE_STATE.Draft;
      push({
        aid: f.aid, fileId: f.fileId, version: f.version, name: f.name, type: f.type, sha256: f.sha256,
        state: st, at: f.at, who: f.uploader, size: f.size, content: f.contentType,
        voided: !!f.voided, source: "task", review: null,
      });
    });
    ((pairDisc && pairDisc.versions) || []).forEach((v) => {
      push({
        aid: v.aid, fileId: v.fileId, version: v.version, name: v.name, type: "", sha256: v.sha256,
        state: discVersionState(v), at: v.at, who: v.submitter, size: v.size,
        content: v.contentType, reviewer: v.reviewer, review: v.review,
        voided: !!v.voided, source: "disc",
      });
    });
    return rows.sort((a, b) => {
      if (!!a.voided !== !!b.voided) return a.voided ? 1 : -1;
      const aa = (a.review || {}).status === "accepted" ? 1 : 0;
      const bb = (b.review || {}).status === "accepted" ? 1 : 0;
      if (aa !== bb) return bb - aa;
      return String(a.at || "").localeCompare(String(b.at || ""));
    });
  };

  /* 每条任务要求行的平铺清单一次算好：渲染与灯箱都读这一份，避免两处各算一遍后顺序不一致。
     依赖里带上 legacy（任务文件在详情接口里）与 stageNode（产物版本在节点接口里），
     上传 / 审核后 reloadStage 会同时刷新两者，清单随之更新。 */
  const submissionRowsByTask = useMemo(() => {
    const out = {};
    const discs = stageNode?.disciplines || [];
    /* 同一产物只在一行里平铺一次：若本阶段有两个任务行映射到同一个产物 key
       （标题重复 / 历史配置），只让第一行带出产物侧版本，避免同样的 v1/v2 列两遍。 */
    const usedDiscKeys = new Set();
    stageRequirements.forEach((r) => {
      if (r.pbDrawings) {
        const memberRows = r.taskIds.flatMap((taskId, index) => {
          const dk = r.discKeys[index];
          const disc = discs.find((x) => x.key === dk);
          if (disc) usedDiscKeys.add(dk);
          // The merged requirement represents drawing task files. Discipline-only
          // submissions (for example a correction response letter) are not drawings.
          return submissionRowsOf(tasksById[taskId], disc)
            .filter((row) => row.source !== "disc")
            .map((row) => ({ ...row, taskId }));
        });
        const merged = mergePbCopies(memberRows, (row) => row.taskId, (row) => row.sha256 || row.fileId);
        out[r.taskId] = merged.map(({ copies }) => {
          const first = copies[0];
          const active = copies.filter((row) => !row.voided);
          const reviews = active.map((row) => row.review?.status).filter(Boolean);
          const state = reviews.includes("needs_fix")
            ? { label: "需整改", cls: "bg-red-100 text-red-700" }
            : reviews.includes("pending")
              ? { label: "待审核", cls: "bg-amber-100 text-amber-800" }
              : reviews.length === r.discKeys.length && reviews.every((status) => status === "accepted")
                ? { label: "审核通过", cls: "bg-emerald-100 text-emerald-700" }
                : reviews.includes("accepted")
                  ? { label: "已提交", cls: "bg-amber-100 text-amber-800" }
                  : first.state;
          return {
            ...first,
            state,
            reviewer: [...new Set(copies.map((row) => row.reviewer).filter(Boolean))].join(" / "),
            voided: active.length === 0,
            type: /cad/i.test(first.type || "") ? "P+B CAD" : /pdf/i.test(first.type || "") ? "P+B PDF" : first.type,
            pbDrawings: true,
          };
        }).sort((a, b) => String(a.at || "").localeCompare(String(b.at || ""))).map((row, index) => ({ ...row, version: index + 1 }));
        return;
      }
      const dk = TASK_TO_DISC[r.title];
      const pairDisc = dk && !usedDiscKeys.has(dk) ? discs.find((x) => x.key === dk) : null;
      if (pairDisc) usedDiscKeys.add(dk);
      out[r.taskId] = submissionRowsOf(tasksById[r.taskId], pairDisc);
    });
    return out;
  }, [stageRequirements, tasksById, stageNode]);

  /* 任务行的版本灯箱：整行所有可预览版本共用一个序列，翻页顺序与屏幕上完全一致。 */
  const submissionLbItems = (rows) => rows.filter((r) => r.fileId).map((r) => ({
    id: r.aid || `${r.version}-${r.at}`,
    fileId: r.fileId,
    size: r.size,
    url: `${process.env.REACT_APP_BACKEND_URL}/api/files/${r.fileId}/download?auth=${authStore.get("ali_token") || ""}`,
    kind: mediaKind(r.name, r.content || ""),
    name: `${r.version == null ? T("参考") : `v${r.version}`} · ${r.name}${r.voided ? T("（已作废）") : ""}`,
    meta: [r.who, uploadDateLabel(r.uploadedAt, r.at)].filter(Boolean).join(" · "),
  }));
  const previewSubmissionRows = previewSubmission
    ? (submissionRowsByTask[previewSubmission.taskId] || [])
    : [];
  const previewSubmissionItems = submissionLbItems(previewSubmissionRows);
  const openSubmissionPreview = (taskId, row) => {
    const items = submissionLbItems(submissionRowsByTask[taskId] || []);
    const idx = items.findIndex((x) => x.fileId === row.fileId);
    if (idx < 0) return;
    setPreviewSubmission({ taskId, index: idx });
  };

  return createPortal(
    <div className="fixed inset-0 z-[70] flex justify-end" data-testid="dv3-stagechat-root">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} data-testid="dv3-stagechat-backdrop" />
      <div className="relative flex h-full w-full max-w-[1180px] flex-col overflow-hidden bg-slate-50 shadow-2xl" data-testid="dv3-stagechat-panel">
        <div className="flex shrink-0 items-center gap-2 border-b border-slate-200 bg-white px-4 py-3">
          <MessageSquareText className="h-4 w-4 text-violet-600" />
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2">
              <b className="truncate text-sm text-slate-900" data-testid="dv3-stagechat-title">{T(stageName)}</b>
              <span className="shrink-0 rounded-full bg-violet-50 px-1.5 py-0.5 text-[9px] font-bold text-violet-700">{T("阶段会话")}</span>
              {/* 合并节点标记：P+B 与固定合并都按实际存在的成员展示，避免新项目
                  只有 building_arch 时也显示成覆盖两个阶段。 */}
              {merged && (
                <span className="shrink-0 rounded-full bg-teal-600 px-1.5 py-0.5 text-[9px] font-bold text-white"
                  data-testid="dv3-stagechat-merged-tag" data-members={memberNodes.map((n) => n.key).join(",")}>
                  {T(group?.key?.startsWith("pb_") ? "P+B 合并 · 覆盖 {0} 个阶段" : "结构 / T24 已并入 · 覆盖 {0} 个阶段", [memberNodes.length])}
                </span>
              )}
            </div>
            <p className="truncate text-[11px] text-slate-500" title={`${project?.projectId || pid} · ${project?.address || ""}`}>
              {project?.projectId || pid}{project?.address ? ` · ${project.address}` : ""}
            </p>
          </div>
          {onOpenProject && (
            <button type="button" onClick={onOpenProject} className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-violet-700" data-testid="dv3-stagechat-project">
              <ChevronRight className="h-3.5 w-3.5" />{T("项目详情")}
            </button>
          )}
          <button type="button" onClick={onClose} className="shrink-0 text-slate-400 hover:text-slate-900" data-testid="dv3-stagechat-close"><X className="h-4 w-4" /></button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
          {/* 2026-10-19 用户需求：聊天输入框固定在抽屉底部，消息列表在上方独立滚动。
              外侧不再整体滚动，否则输入框会随消息一起滚走，不像聊天窗口。 */}
          <div className="min-w-0 flex min-h-0 flex-1 flex-col p-4" data-testid="dv3-stagechat-thread">
            {err && <p className="mb-3 text-xs text-red-600">{err}</p>}
            {!project && !err && <p className="flex items-center gap-1 text-xs text-slate-400"><Loader2 className="h-4 w-4 animate-spin" />{T("正在读取阶段会话")}</p>}
            {project && (
              <Comments p={project} meta={meta} api={api} refresh={() => { loadProject(); loadNodes(); onChanged?.(); }}
                /* 合并节点把组内全部阶段一起传进去：读 = 组内评论都显示（改造前两个阶段
                   各有各的会话，历史讨论不能因为合并而消失），写 = 收敛到主阶段（见 Comments 注释）。 */
                fixedStage={merged ? memberKeys.join(",") : stageKey} hideLibrary tall composerBottom ownerOptions={project.effectiveManagers || []}
                locked={chatLock.allowed === false} lockReason={chatLock.reason || ""} />
            )}
          </div>

          <aside className="h-[42vh] w-full shrink-0 overflow-y-auto border-t border-slate-200 bg-white p-3 lg:h-auto lg:w-[340px] lg:border-l lg:border-t-0" data-testid="dv3-stagechat-products">
            <div className="flex items-center gap-1">
              <GitBranch className="h-3.5 w-3.5 shrink-0 text-violet-600" />
              <p className="min-w-0 flex-1 truncate text-[11px] font-bold text-slate-600">{T("阶段所需产物")}</p>
              {/* 批量操作（2026-10-19 用户需求）：点开多选清单，默认全部勾选；
                  取消勾选 = 该产物本单无需提交，不影响阶段推进，可随时在下方恢复。
                  只在**还有可批量决定的产物**时出现（2026-10-21）：若本阶段产物全部
                  由任务要求行承载（例如方案设计只剩 Floor Plan），弹窗会是空的，
                  留着入口只会让人点进去发现没东西可改。 */}
              {batchScopeProducts.length > 0 && (
                <button type="button" onClick={openBatch} disabled={!meta?.canEdit || savingSkip}
                  title={T("批量设置：勾选=需要提交；取消勾选=本阶段无需提交，不影响流程推进")}
                  data-testid="dv3-stagechat-batch-open"
                  className="inline-flex shrink-0 items-center gap-0.5 rounded border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[9px] font-semibold text-violet-700 hover:bg-violet-100 disabled:opacity-50">
                  <Settings2 className="h-3 w-3" />{T("批量操作")}
                </button>
              )}
            </div>
            {nodes === null ? (
              <p className="mt-2 flex items-center gap-1 text-[10px] text-slate-400"><Loader2 className="h-3 w-3 animate-spin" />{T("正在读取产物")}</p>
            ) : !stageNode ? (
              <p className="mt-2 text-[10px] text-slate-400" data-testid="dv3-stagechat-products-empty">{T("当前阶段无关联产物")}</p>
            ) : (
              <div className="mt-2 space-y-2">
                <section className="rounded-md border border-violet-300 bg-violet-50/40"
                  data-testid={`dv3-stagechat-products-${stageNode.key}`} data-current="1">
                  <div className="flex items-center gap-1 border-b border-slate-100 px-2 py-1.5">
                    <span className="min-w-0 flex-1 truncate text-[10px] font-bold text-slate-700" title={T(stageName)}>{T(stageName)}</span>
                    {stageNode.current && <span className="shrink-0 rounded-full bg-violet-600 px-1.5 py-0.5 text-[9px] font-bold text-white">{T("当前")}</span>}
                    {/* 「N 项」只统计当前真正画出来的产物（需要提交且未被任务卡承载）；
                        无需提交的收在下方折叠区单独标注。 */}
                    {visibleProducts.length > 0 && <span className="shrink-0 text-[9px] tabular-nums text-slate-400">{T("{0} 项", [visibleProducts.length])}</span>}
                  </div>
                  <div className="space-y-1 p-1.5">
                    {/* 隐藏文件选择：每个产物的「上传图纸 / 传新版」都复用同一个 input，
                        当前目标产物放在 uploadFor 里，避免 13 个产物各挂一个隐藏控件。 */}
                    <input ref={uploadRef} type="file" className="hidden"
                      accept={DESIGN_UPLOAD_ACCEPT}
                      data-testid="dv3-stagechat-product-upload-input"
                      onChange={uploadVersion} />
                    {/* 阶段产物门禁（2026-10-19 用户需求）：在阶段里直接看到
                        「几项已审核通过 / 还差哪几项」，不用等点「完成阶段」才被 400 拦住。

                        【2026-10-22 P+B 合并】合并节点的计数 = 组内全部阶段的产物合计；
                        「完成节点」按钮一次关掉组内全部未完成阶段（见 closeStage）。
                        唯一例外是审图节点（pb_review）：它的完成入口是 City 块上的「已通过」
                        （一次关两个阶段 + 必须传 City 结论附件），这里不再给第二个按钮 ——
                        留着会出现「同一件事两个入口，点这个能绕过结论附件校验」。 */}
                    {stageNode.productGate && stageNode.productGate.total > 0 && (
                      <div className="mb-1 rounded border px-2 py-1 text-[9px] font-semibold"
                        data-testid={`dv3-stagechat-gate-${stageNode.key}`}
                        data-ok={stageNode.productGate.ok ? "1" : "0"}
                        data-approved={stageNode.productGate.approved}
                        data-total={stageNode.productGate.total}>
                        <div className="flex items-start gap-1">
                          <span className={`min-w-0 flex-1 ${stageNode.productGate.ok ? "text-emerald-700" : "text-amber-700"}`}>
                            {stageNode.productGate.ok
                              ? T("产物已全部完成（{0}/{0}），可完成本阶段", [stageNode.productGate.total])
                              : T("产物完成进度 {0}/{1} · 还差 {2} 项", [stageNode.productGate.approved, stageNode.productGate.total, stageNode.productGate.blocking.length])}
                          </span>
                          {/* 「完成阶段」按钮（2026-10-21 恢复）：用户补充「这个每个阶段的关闭按钮
                              还是需要，这个关闭阶段其实就是完成阶段的意思」。与自动推进并存：
                              自动推进只处理「当前阶段且门禁全过」，本单不做某项产物 / 审批改豁免
                              这类判断只能由人确认；未通过时后端返回 blocking 明细，经理可填原因强关。 */}
                          {meta?.isManager && !stageNode.done && !reviewNode && (
                            <button type="button" disabled={closingStage || stageTooEarly}
                              onClick={() => closeStage(false)}
                              title={stageTooEarly ? T("流程还没走到本阶段，请先完成当前阶段") : (merged ? T("把本节点内的 {0} 个阶段一起标记为已完成并激活下一节点", [stageNode.stageIds.length]) : T("把本阶段标记为已完成并激活下一阶段"))}
                              data-testid={`dv3-stagechat-close-stage-${stageNode.key}`}
                              data-too-early={stageTooEarly ? "1" : "0"}
                              className="inline-flex shrink-0 items-center gap-0.5 rounded border border-violet-300 bg-white px-1.5 py-0.5 text-[9px] font-semibold text-violet-700 hover:bg-violet-50 disabled:opacity-50">
                              {closingStage ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <CheckCircle2 className="h-2.5 w-2.5" />}
                              {merged ? T("完成该节点") : T("完成该阶段")}
                            </button>
                          )}
                          {/* 「回退阶段」按钮（2026-10-23 用户需求）：已完成时出现在同一位置，
                              与「完成该阶段」互斥 —— 一个只在本阶段没做完时出现，一个只在已完成时出现。
                              审图节点（reviewNode）不在这里：它的完成入口是 City 块的「已通过」，
                              回退同样应走 City 块，避免两个入口各退一半。 */}
                          {meta?.isManager && REOPENABLE_STATUSES.some((x) => stageNode.stageIds?.some((t) => x === t.status)) && !reviewNode && (
                            <button type="button" disabled={closingStage}
                              onClick={reopenStage}
                              title={T("把这个阶段退回「进行中」，用于方案返工重做（已交文件与版本历史保留）")}
                              data-testid={`dv3-stagechat-reopen-stage-${stageNode.key}`}
                              className="inline-flex shrink-0 items-center gap-0.5 rounded border border-amber-300 bg-white px-1.5 py-0.5 text-[9px] font-semibold text-amber-700 hover:bg-amber-50 disabled:opacity-50">
                              {closingStage ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <RotateCcw className="h-2.5 w-2.5" />}
                              {merged ? T("回退该节点") : T("回退阶段")}
                            </button>
                          )}
                        </div>
                        {/* 规则文案按当前阶段的产物配置动态生成：全部「无需审核」时
                            继续说「审核人通过」会误导用户去等一个不存在的审核。 */}
                        <p className="mt-0.5 font-normal text-slate-500">
                          {activeProducts.some((x) => x.reviewRequired !== false)
                            ? T("规则：提交 → 审核人通过 → 阶段才可完成")
                            : T("规则：本阶段产物均无需审核，提交即完成")}
                        </p>
                      </div>
                    )}
                    {activeProducts.length === 0 && stageRequirements.length === 0 && <p className="px-1 py-1 text-[9px] text-slate-400">{T("本阶段无关联产物")}</p>}
                    {/* 没有挂载产物的阶段（permit-only 等精简流程）同样要能从会话里完成阶段，
                        否则经理还得回详情页 —— 会话是列表页的主要入口，入口不能断（2026-10-21 恢复）。
                        合并节点同样跳过：审图节点由 City 块收口（见上方注释）。 */}
                    {(!stageNode.productGate || stageNode.productGate.total === 0) && meta?.isManager && !stageNode.done && !reviewNode && (
                      <div className="mb-1 flex items-center justify-end">
                        <button type="button" disabled={closingStage || stageTooEarly}
                          onClick={() => closeStage(false)}
                          title={stageTooEarly ? T("流程还没走到本阶段，请先完成当前阶段") : (merged ? T("把本节点内的 {0} 个阶段一起标记为已完成并激活下一节点", [stageNode.stageIds.length]) : T("把本阶段标记为已完成并激活下一阶段"))}
                          data-testid={`dv3-stagechat-close-stage-${stageNode.key}`}
                          data-too-early={stageTooEarly ? "1" : "0"}
                          className="inline-flex shrink-0 items-center gap-0.5 rounded border border-violet-300 bg-white px-1.5 py-0.5 text-[9px] font-semibold text-violet-700 hover:bg-violet-50 disabled:opacity-50">
                          {closingStage ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <CheckCircle2 className="h-2.5 w-2.5" />}
                          {merged ? T("完成该节点") : T("完成该阶段")}
                        </button>
                      </div>
                    )}
                    {/* 无挂载产物的阶段（permit-only 等）同样要能回退 —— 它与上面「完成该阶段」
                        是互斥的两种状态，漏了这条分支就会出现「这类阶段完成了却退不回来」。
                        容器条件不重复 stageNode.done：回退的对象本来就是已完成阶段。 */}
                    {(!stageNode.productGate || stageNode.productGate.total === 0) && meta?.isManager
                      && stageNode.stageIds?.some((t) => REOPENABLE_STATUSES.includes(t.status)) && !reviewNode && (
                      <div className="mb-1 flex items-center justify-end">
                        <button type="button" disabled={closingStage}
                          onClick={reopenStage}
                          title={T("把这个阶段退回「进行中」，用于方案返工重做（已交文件与版本历史保留）")}
                          data-testid={`dv3-stagechat-reopen-stage-${stageNode.key}`}
                          className="inline-flex shrink-0 items-center gap-0.5 rounded border border-amber-300 bg-white px-1.5 py-0.5 text-[9px] font-semibold text-amber-700 hover:bg-amber-50 disabled:opacity-50">
                          {closingStage ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <RotateCcw className="h-2.5 w-2.5" />}
                          {merged ? T("回退该节点") : T("回退阶段")}
                        </button>
                      </div>
                    )}
                    {visibleProducts.map((item) => {
                      /* 2026-10-19 用户需求：产物在阶段里就要能控制 ——
                         「对应人完成提交以后需要对应人的一个审核，审核完成才能完成当前阶段」。
                         因此每行直接展示：提交人 / 审核人 / 审核状态，并在有权限时
                         就地提供「提交」「审核通过」「退回重做」，不再要求用户先跳去别处。 */
                      const parties = item.parties || {};
                      const sub = parties.submitter || {};
                      const rev = parties.reviewer || {};
                      const pend = !!item.reviewPending;
                      const approved = !!item.approved;
                      const versions = item.versions || [];
                      /* 该产物是否需要审核（2026-10-19 用户需求）：来自全局配置
                         「产物提交 / 审核」。为 false 时提交即完成，页面不显示审核人，
                         也不显示「待审核」，避免用户以为还在等谁复核。 */
                      const reviewRequired = item.reviewRequired !== false;
                      const canSubmit = !!meta?.canEdit && !approved && ["not_started", "waiting_site_measure", "preparing", "requested", "working", "correction", "stuck"].includes(item.status)
                        && (meta?.isManager || (sub.username && sub.username === meta?.me?.username));
                      /* 「无需审核」产物的换版权（2026-10-21 用户需求）。
                         用户原话：「包括那种不需要审核的上传完后对应的负责人也能去修改」。
                         背景：免审核的产物一上传就直接 done，上方 canSubmit 随即为 false、
                         上传按钮整段消失，负责人发现传错也没有任何可点的入口 ——
                         这是「给了状态就不能编辑」在免审核场景下的具体表现。
                         口径：只对 **免审核 + 已完成** 的产物开放，且只给该产物的负责人
                         （提交人 / 曾上传过该产物任一版本的人 / 设计主管），避免任何人随手改已完成交付物。
                         不要求 item.files > 0：负责人可能先把错版全部作废、再想传正确的，
                         若仍卡「必须有有效文件」就会形成死锁（既不能换版、也不能重传）。
                         换版不重新走审核（本来就无需审核），历史版本仍在，可逐版对照与作废。 */
                      const canReplaceNoReview = !!meta?.canEdit && approved && !reviewRequired
                        && (meta?.isManager
                          || (sub.username && sub.username === meta?.me?.username)
                          || versions.some((v) => v.uploaderUsername && v.uploaderUsername === meta?.me?.username));
                      // 审核权限与后端 _can_review 同口径：设计主管 / 该产物配置的审核人 / 美国建筑设计师。
                      const canReview = pend && (meta?.isManager || meta?.me?.role === "us_policy" || (rev.username && rev.username === meta?.me?.username));
                      return (
                        <div key={item.key}
                          title={`${T(item.name)} · ${T(item.label || item.status)}`}
                          data-testid={`dv3-stagechat-product-${stageNode.key}-${item.key}`}
                          data-approved={approved ? "1" : "0"}
                          data-review-pending={pend ? "1" : "0"}
                          data-version-count={versions.length}
                          className={`rounded border bg-white px-2 py-1.5 ${approved ? "border-emerald-200 bg-emerald-50/40" : pend ? "border-amber-200 bg-amber-50/30" : "border-slate-200"}`}>
                          <div className="flex w-full items-center gap-1.5">
                            <Package className={`h-3 w-3 shrink-0 ${approved ? "text-emerald-600" : "text-violet-500"}`} />
                            <span className="min-w-0 flex-1 truncate text-[10px] font-semibold text-slate-700">{T(item.name)}</span>
                            {/* 版本计数（2026-10-21 用户反馈「有的需要点击才显示在一个按钮上」）：
                                这里只作**统计**，不是折叠入口 —— 全部版本已在下方的
                                dv3-stagechat-versions-* 清单里逐条平铺展开，无需点击。
                                文案用「共 N 版」而不是「N 版」，避免被读成一个可点的历史按钮。 */}
                            {versions.length > 0 && (
                              <span className="shrink-0 rounded bg-slate-100 px-1 py-0.5 text-[9px] font-bold tabular-nums text-slate-600"
                                title={T("下方已逐版列出全部 {0} 个版本", [versions.length])}
                                data-testid={`dv3-stagechat-version-count-${item.key}`}>
                                {T("共 {0} 版", [versions.length])}
                              </span>
                            )}
                            {approved && <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-600" aria-hidden="true" />}
                            <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold ${statusClass(item.status)}`}>{T(item.label || STATUS_LABEL[item.status] || item.status)}</span>
                          </div>
                          <p className="mt-0.5 pl-[18px] text-[9px] text-slate-500">
                            {T("提交人")} <b className="font-semibold text-slate-700">{sub.name || T("未配置")}</b>
                            {reviewRequired ? (<>
                              <span className="mx-0.5 text-slate-300">·</span>
                              {T("审核人")} <b className="font-semibold text-slate-700">{rev.name || T("设计部经理")}</b>
                            </>) : (
                              <span className="ml-1 rounded bg-emerald-100 px-1 py-0.5 text-[9px] font-bold text-emerald-800"
                                data-testid={`dv3-stagechat-no-review-${item.key}`}>{T("· 无需审核")}</span>
                            )}
                            {pend && <span className="ml-1 font-bold text-amber-700">{T("· 待审核")}</span>}
                            {approved && <span className="ml-1 font-bold text-emerald-700">{T("· 已通过")}</span>}
                            {/* 设计经理可就地配置该产物的提交人 / 审核人（2026-10-19 用户需求）。
                                普通设计师看不到这个入口，避免自己给自己配审核人。 */}
                            {meta?.isManager && (
                              <button type="button"
                                onClick={() => {
                                  if (partyEdit === item.key) return setPartyEdit(null);
                                  setPartyEdit(item.key);
                                  setPartySub(item.submitterUsername || "");
                                  setPartyRev(item.reviewerUsername || "");
                                }}
                                title={T("配置该产物的提交人与审核人")}
                                data-testid={`dv3-stagechat-party-edit-${item.key}`}
                                className="ml-1 inline-flex shrink-0 items-center rounded p-0.5 text-slate-400 hover:bg-violet-50 hover:text-violet-700">
                                <Settings2 className="h-2.5 w-2.5" />
                              </button>
                            )}
                          </p>
                          {partyEdit === item.key && meta?.isManager && (
                            <div className="mt-1 ml-[18px] space-y-1 rounded border border-violet-200 bg-violet-50/60 p-1.5"
                              data-testid={`dv3-stagechat-party-cfg-${item.key}`}>
                              <label className="flex items-center gap-1 text-[9px] text-slate-600">
                                <span className="w-8 shrink-0">{T("提交人")}</span>
                                <select value={partySub} onChange={(e) => setPartySub(e.target.value)}
                                  data-testid={`dv3-stagechat-party-submitter-${item.key}`}
                                  className="min-w-0 flex-1 rounded border border-slate-200 bg-white px-1 py-0.5 text-[9px]">
                                  <option value="">{T("按项目设计师自动")}</option>
                                  {(meta?.designers || []).filter((u) => u.username).map((u) => (
                                    <option key={u.username} value={u.username}>{u.nameZh || u.name || u.username}</option>
                                  ))}
                                </select>
                              </label>
                              <label className="flex items-center gap-1 text-[9px] text-slate-600">
                                <span className="w-8 shrink-0">{T("审核人")}</span>
                                <select value={partyRev} onChange={(e) => setPartyRev(e.target.value)}
                                  data-testid={`dv3-stagechat-party-reviewer-${item.key}`}
                                  className="min-w-0 flex-1 rounded border border-slate-200 bg-white px-1 py-0.5 text-[9px]">
                                  <option value="">{T("US 设计师 / 设计部经理")}</option>
                                  {(meta?.designers || []).filter((u) => u.username).map((u) => (
                                    <option key={u.username} value={u.username}>{u.nameZh || u.name || u.username}</option>
                                  ))}
                                </select>
                              </label>
                              <div className="flex items-center justify-end gap-1">
                                <button type="button" onClick={() => setPartyEdit(null)}
                                  className="rounded px-1.5 py-0.5 text-[9px] text-slate-500 hover:text-slate-800">{T("取消")}</button>
                                <button type="button" disabled={savingParty} onClick={() => saveParty(item)}
                                  data-testid={`dv3-stagechat-party-save-${item.key}`}
                                  className="rounded bg-violet-600 px-1.5 py-0.5 text-[9px] font-semibold text-white hover:bg-violet-700 disabled:opacity-50">
                                  {savingParty ? T("保存中…") : T("保存")}
                                </button>
                              </div>
                            </div>
                          )}
                          {/* 多轮产物逐版列出（2026-10-19 用户需求）：
                              「如果产物进行了多轮，需要将产物全部列出来 v1/v2 这种版本，
                               然后提交人、审核人都要显示出来」。
                              为什么默认展开：阶段会话就是用来判断「这一轮到底交没交、谁审的」，
                              折叠起来又要多点一次；版本数量通常 1~5 个，展开不会淹没其它产物。
                              已作废版本保留（历史永久留档红线），用删除线弱化并标注原因。
                              排序（2026-10-20 用户需求 · 与详情页同一口径）：审批通过的版本在最前，
                              其余按 v1 / v2 正序，作废件沉底 —— 见 sortFileVersions 注释。 */}
                          {versions.length > 0 && (
                            <div className="mt-1 space-y-0.5 rounded border border-slate-100 bg-slate-50/60 px-1.5 py-1"
                              data-testid={`dv3-stagechat-versions-${item.key}`}>
                              {sortFileVersions(versions).map((v) => {
                                const rv = v.review || null;
                                const st = rv?.status;
                                /* 版本状态文案（2026-10-19 用户需求澄清）。
                                   用户描述的流程是「指定提交人提交 → 通知审核人 → 审核 / 驳回 →
                                   再传第二版 → 再审核」，这条链路里每一版都由一次提交产生，
                                   不存在「未提交 / 未送审」这种待办状态。因此不再用「未送审」，
                                   而是按版本的真实归属显示：
                                     · phase=historical：审核流程上线前已上传的历史图纸，仅留档；
                                     · phase=draft：制作阶段的拖拽工作稿，未进入审核；
                                     · 有复核记录的按记录显示待审核 / 通过 / 退回 / 被新版替代。 */
                                const phase = v.phase || (rv ? "reviewed" : "historical");
                                /* 状态文案与颜色统一由 discVersionState 提供（2026-10-21）：
                                   任务行的平铺清单读的是同一个函数，改一处两处一起变，
                                   不会出现同一版在产物卡里写「审核通过」、在任务行里写别的。 */
                                const vs = discVersionState(v);
                                const stLabel = T(vs.label);
                                const stCls = vs.cls;
                                // 2026-10-20 修复：原来版本文件名 / 下载图标都指向后端 download 接口并 target=_blank，
                                // 后端返回 inline 的 zip / CAD / 大 PDF 会让浏览器留下一个空白标签页。
                                // 现在文件名 → 页内灯箱预览（PDF 走逐页渲染），下载图标 → blob 直接落盘。
                                const fUrl = v.fileId ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${v.fileId}/download?auth=${authStore.get("ali_token") || ""}` : null;
                                return (
                                  <div key={v.aid || `${v.version}-${v.at}`}
                                    data-testid={`dv3-stagechat-version-${item.key}-${v.version}`}
                                    data-version={v.version}
                                    data-voided={v.voided ? "1" : "0"}
                                    data-review-status={st || ""}
                                    data-version-phase={phase}
                                    className={`rounded px-1 py-0.5 text-[9px] leading-4 ${v.voided ? "opacity-60" : ""}`}>
                                    <div className="flex items-center gap-1">
                                      <span className={`shrink-0 rounded px-1 text-[8px] font-bold ${v.voided ? "bg-slate-400 text-white" : "bg-slate-700 text-white"}`}>
                                        {v.version == null ? T("参考") : `v${v.version}`}
                                      </span>
                                      {fUrl ? (
                                        <button type="button" onClick={() => openVersionPreview(item, v)} title={T("页内预览：{0}", [v.name])}
                                          data-testid={`dv3-stagechat-version-open-${item.key}-${v.version}`}
                                          className={`min-w-0 flex-1 truncate text-left font-semibold ${v.voided ? "text-slate-400 line-through" : "text-violet-700 hover:underline"}`}>
                                          {v.name}
                                        </button>
                                      ) : <span className="min-w-0 flex-1 truncate font-semibold text-slate-600">{v.name}</span>}
                                      <span className={`shrink-0 rounded px-1 py-0.5 font-bold ${stCls}`}>{stLabel}</span>
                                    </div>
                                    {/* 每一轮都显示当时的提交人 / 审核人：历史轮次直接用复核记录里的人，
                                        不受后来改配置影响，才能还原「这一轮是谁交、谁审的」。
                                        没有复核记录的版本不显示审核人姓名（避免被读成「这个人已经审过这一版」），
                                        改为一句话说明它属于历史留档还是制作中的工作稿。 */}
                                    <div className="flex flex-wrap items-center gap-x-1 pl-[22px] text-slate-500">
                                      <span>{T("提交")} <b className="font-semibold text-slate-700">{v.submitter || T("—")}</b></span>
                                      <span className="text-slate-300">·</span>
                                      {rv ? (
                                        <span>{T("审核")} <b className="font-semibold text-slate-700">{v.reviewer || T("设计部经理")}</b></span>
                                      ) : phase === "rework" ? (
                                        <span className="text-orange-700">{T("整改期间上传的返工稿，需重新送审")}</span>
                                      ) : phase === "draft" ? (
                                        <span className="text-sky-700">{T("制作中上传的工作稿，不进入审核")}</span>
                                      ) : (
                                        <span className="text-slate-400">{T("审核流程上线前上传，仅历史留档")}</span>
                                      )}
                                      {st === "superseded" && (rv?.supersededBy === "stage_reopen"
                                        /* 因阶段回退作废：这不是正常迭代，要明确告诉用户「这一版要重交」，
                                           否则会以为审核流程还在走、在等某个人复核。 */
                                        ? <span className="font-semibold text-rose-700">{T("· 因阶段回退作废，需重新提交送审")}</span>
                                        : <span className="text-slate-500">{T("· 重新提交为 v{0}，本版不再等待审核", [rv?.supersededByVersion ?? "—"])}</span>)}
                                      {st === "pending" && rv?.waitingHours != null && <span className="font-bold text-amber-700">{T("· 已等待 {0} 小时", [rv.waitingHours])}</span>}
                                      {st === "needs_fix" && rv?.comment && <span className="truncate text-red-600" title={rv.comment}>{T("· {0}", [rv.comment])}</span>}
                                      {st === "accepted" && rv?.reviewedBy && <span className="text-emerald-700">{T("· {0} 通过", [rv.reviewedBy])}</span>}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-x-1 pl-[22px] text-slate-400">
                                      <FileUploadDate value={v.uploadedAt} fallbackValue={v.at} />
                                      {v.voided && <span className="text-red-500">{T("· 已作废：{0}", [v.voidReason || ""])}</span>}
                                      {v.note && <span className="truncate" title={v.note}>{T("· {0}", [v.note])}</span>}
                                      {fUrl && (
                                        <button type="button" title={T("下载到本地")}
                                          data-testid={`dv3-stagechat-version-download-${item.key}-${v.version}`}
                                          onClick={() => downloadFile(v.fileId, v.name).catch((e) => toast.error(e?.message || T("下载失败")))}
                                          className="shrink-0 text-slate-400 hover:text-violet-700">
                                          <Download className="inline h-2.5 w-2.5" />
                                        </button>
                                      )}
                                      {/* 传错版本的就地作废（2026-10-21 用户需求）。
                                          阶段会话是多轮交付的主战场，错版在这里就能处理，不用特意翻到项目详情。
                                          权限与详情页 / 后端 _void_disc_file 完全同一口径：
                                          上传人 / 该产物配置的提交人或审核人 / 设计主管；
                                          「无需审核」的产物没有审核人，但提交人与上传人仍可作废，
                                          正好覆盖用户说的「不需要审核的上传完负责人也能改」。
                                          作废只打标记（fileId 与原因永久留档），不做物理删除。 */}
                                      {canVoidVersion(item, v) && (
                                        <button type="button" title={T("作废该版本（记录仍留档）")}
                                          disabled={voidingVersion === v.aid}
                                          data-testid={`dv3-stagechat-version-void-${item.key}-${v.version}`}
                                          onClick={() => voidVersion(item, v)}
                                          className="shrink-0 text-slate-400 hover:text-red-600 disabled:opacity-40">
                                          {voidingVersion === v.aid ? <Loader2 className="inline h-2.5 w-2.5 animate-spin" /> : <Trash2 className="inline h-2.5 w-2.5" />}
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {(canSubmit || canReview || canReplaceNoReview || (meta?.canEdit && !approved)) && (
                            <div className="mt-1 flex flex-wrap items-center gap-1 pl-[18px]">
                              {/* 有图纸才能提交；没有图纸时直接给「上传图纸」，避免用户点了提交
                                  只收到「请先上传」的报错（后端 submit 会拦住空版本提交）。
                                  用节点接口的 files 计数（已排除作废 / 删除），不数 versions ——
                                  历史合并附件等条目不该被当成可提交的图纸。 */}
                              {meta?.canEdit && !approved && !(item.files > 0) ? (
                                <button type="button" disabled={busyProduct === item.key}
                                  onClick={() => pickVersion(item)}
                                  data-testid={`dv3-stagechat-upload-${item.key}`}
                                  className="inline-flex items-center gap-0.5 rounded border border-violet-300 bg-violet-50 px-1.5 py-0.5 text-[9px] font-semibold text-violet-700 hover:bg-violet-100 disabled:opacity-50">
                                  {busyProduct === item.key ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <UploadCloud className="h-2.5 w-2.5" />}
                                  {T("上传图纸")}
                                </button>
                              ) : canSubmit && (
                                <button type="button" disabled={busyProduct === item.key}
                                  onClick={() => submitProduct(item)}
                                  data-testid={`dv3-stagechat-submit-${item.key}`}
                                  className="inline-flex items-center gap-0.5 rounded border border-violet-300 bg-violet-50 px-1.5 py-0.5 text-[9px] font-semibold text-violet-700 hover:bg-violet-100 disabled:opacity-50">
                                  {busyProduct === item.key ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <UploadCloud className="h-2.5 w-2.5" />}
                                  {T("提交")}
                                </button>
                              )}
                              {/* 驳回 / 整改后重传：同一入口，上传即把这一轮送审（见 uploadVersion）。 */}
                              {canSubmit && item.files > 0 && (
                                <button type="button" disabled={busyProduct === item.key}
                                  onClick={() => pickVersion(item)}
                                  title={T("上传新版本并重新送审")}
                                  data-testid={`dv3-stagechat-reupload-${item.key}`}
                                  className="inline-flex items-center gap-0.5 rounded border border-slate-300 bg-white px-1.5 py-0.5 text-[9px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                                  <UploadCloud className="h-2.5 w-2.5" />{T("传新版")}
                                </button>
                              )}
                              {/* 免审核产物完成后的换版入口（2026-10-21）。文案刻意写「换一版」
                                  而不是「重新送审」——这个产物本来就不走审核，不该给出会等待审核的暗示。 */}
                              {canReplaceNoReview && (
                                <button type="button" disabled={busyProduct === item.key}
                                  onClick={() => pickVersion(item)}
                                  title={T("该产物无需审核，换版后直接生效（历史版本仍留档）")}
                                  data-testid={`dv3-stagechat-replace-${item.key}`}
                                  className="inline-flex items-center gap-0.5 rounded border border-slate-300 bg-white px-1.5 py-0.5 text-[9px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                                  <UploadCloud className="h-2.5 w-2.5" />{T("换一版（改错）")}
                                </button>
                              )}
                              {canReview && <>
                                <button type="button" disabled={busyProduct === item.key}
                                  onClick={() => decideProduct(item, "accepted")}
                                  data-testid={`dv3-stagechat-accept-${item.key}`}
                                  className="inline-flex items-center gap-0.5 rounded border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50">
                                  <CheckCircle2 className="h-2.5 w-2.5" />{T("审核通过")}
                                </button>
                                <button type="button" disabled={busyProduct === item.key}
                                  onClick={() => decideProduct(item, "needs_fix")}
                                  data-testid={`dv3-stagechat-reject-${item.key}`}
                                  className="inline-flex items-center gap-0.5 rounded border border-red-300 bg-red-50 px-1.5 py-0.5 text-[9px] font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50">
                                  <RotateCcw className="h-2.5 w-2.5" />{T("退回")}
                                </button>
                              </>}
                            </div>
                          )}
                          {/* 产物卡页脚的机构（2026-10-22 与详情对齐）。
                              详情阶段卡把「产物 ↔ 机构」映射到本阶段产物的机构，直接铺在产物卡底部
                              （如 Structural 卡下的「Structural Engineering」、Building 卡下的 MEP）。
                              会话右侧原先没有这一层，而这些机构又因为「已挂在产物下」被排除在
                              「其他机构对接」之外，结果是两头都看不到 —— 开发库实测
                              structural_t24 的结构工程 / Title 24 两家机构就此完全消失。
                              这里按详情同一口径在卡内补页脚，`其他机构对接` 仍只收没有产物归属的。 */}
                          {(() => {
                            const mine = stageAgencies.filter((a) => agToDisc[a.type] === item.key);
                            if (!mine.length) return null;
                            return (
                              <div className="mt-1.5 space-y-1 border-t border-slate-200/70 pt-1.5"
                                data-testid={`dv3-stagechat-ags-${stageNode.key}-${item.key}`}>
                                {mine.map((a) => (
                                  <AgencyRow key={a.id} a={a} meta={meta} api={api} designers={designers} onChanged={reloadStage} compact docs={agencyDocs[a.type] || []} resp={legacy?.agencyResp || {}} />
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                    {/* 配对产物的口径（2026-10-22 用户最终确认：
                        「Site Plan + Floor Plan (CAD/PDF) 这种两个…保留我发的这个就行…
                        详情里的显示不是已经删除了另外一个了吗为什么又写回列表这边来了」）。

                        正确口径 = **一份交付只出现一次**，保留任务要求行（它带负责人 / 上传 /
                        文件与版本 / 送审 / 审核通过 / 退回重做），配对的产物卡不在这里重复列。
                        这与详情阶段卡完全一致：详情把「审核通过 / 退回重做」搬到了任务卡上
                        （见 LegacyTabs 的 dv3-ltask-pairdisc-review-*），产物卡整张收起。

                        我前一版（2026-10-21 第三次调整）把产物卡加了回来并标「任务区同办」，
                        那是错的 —— 用户看到的仍是同一份交付出现两次（Floor Plan 卡 + 
                        Site Plan + Floor Plan 任务行），所以这里撤回。

                        不丢信息：下面那行汇总仍列出被收起的产物状态（含待复核提示），
                        审核动作在任务行上，与详情同一套。 */}
                    {activeProducts.some((item) => carriedProductKeys.has(item.key)) && (
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded border border-dashed border-slate-200 bg-slate-50/70 px-1.5 py-1"
                        data-testid={`dv3-stagechat-carried-${stageNode.key}`}>
                        <span className="shrink-0 text-[9px] font-semibold text-slate-500">{T("已在上面任务内办理")}</span>
                        {activeProducts.filter((item) => carriedProductKeys.has(item.key)).map((item) => (
                          <span key={item.key} className="inline-flex min-w-0 items-center gap-1" data-testid={`dv3-stagechat-carried-${stageNode.key}-${item.key}`} data-status={item.status}>
                            {item.approved
                              ? <CheckCircle2 className="h-2.5 w-2.5 shrink-0 text-emerald-600" />
                              : <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${item.reviewPending ? "bg-amber-500" : "bg-slate-300"}`} />}
                            <span className="truncate text-[9px] font-semibold text-slate-600" title={T(item.name)}>{T(item.name)}</span>
                            <span className={`shrink-0 rounded-full px-1 text-[9px] font-bold ${statusClass(item.status)}`}>{T(item.label || STATUS_LABEL[item.status] || item.status)}</span>
                            {item.reviewPending && <span className="shrink-0 text-[9px] font-semibold text-amber-700">{T("待复核")}</span>}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* 无需提交的产物（2026-10-19 用户需求）：默认收起，避免和真正要交的产物混在一起；
                        展开后可看到每项的状态与「恢复」按钮，恢复后回到上方主列表。 */}
                    {skippedProducts.length > 0 && (
                      <div className="mt-1.5 border-t border-dashed border-slate-300 pt-1.5" data-testid={`dv3-stagechat-skipped-${stageNode.key}`}>
                        <button type="button" onClick={() => setShowSkipped((v) => !v)}
                          className="flex w-full items-center gap-1 rounded px-1 py-0.5 text-[9px] font-bold text-slate-500 hover:text-slate-800"
                          title={T("这些产物已设为无需提交，不影响流程推进；可展开后恢复")}
                          data-testid="dv3-stagechat-skipped-toggle" data-open={showSkipped ? "1" : "0"}>
                          {showSkipped ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                          <span className="flex-1 text-left">{T("无需提交（{0}）", [skippedProducts.length])}</span>
                        </button>
                        {showSkipped && (
                          <div className="mt-1 space-y-1">
                            {skippedProducts.map((item) => (
                              <div key={item.key} className="flex items-center gap-1.5 rounded border border-dashed border-slate-200 bg-slate-50 px-2 py-1"
                                data-testid={`dv3-stagechat-skipped-item-${item.key}`}>
                                <Package className="h-3 w-3 shrink-0 text-slate-400" />
                                <span className="min-w-0 flex-1 truncate text-[10px] font-semibold text-slate-500 line-through decoration-slate-300" title={T(item.name)}>{T(item.name)}</span>
                                <span className="shrink-0 rounded-full bg-slate-200 px-1.5 py-0.5 text-[9px] font-bold text-slate-600">{T("无需提交")}</span>
                                {meta?.canEdit && (
                                  <button type="button" onClick={() => restoreProduct(item.key)} disabled={savingSkip}
                                    title={T("恢复为需要提交")}
                                    data-testid={`dv3-stagechat-restore-${item.key}`}
                                    className="inline-flex shrink-0 items-center gap-0.5 rounded border border-violet-200 bg-white px-1 py-0.5 text-[9px] font-semibold text-violet-700 hover:bg-violet-50 disabled:opacity-50">
                                    <RotateCcw className="h-2.5 w-2.5" />{T("恢复")}
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {/* 详情阶段卡里的子任务要求文件：未开始阶段没有关联产物时，这里仍要列出本阶段所需文件。
                        2026-10-19 用户反馈后，这里不再只是只读展示，而是把详情任务卡的操作入口搬进来：
                        负责人 / 快速上传 / 文件与版本。状态改为只读（见下）。
                        2026-10-21 终版口径：**任务要求行全部保留**（用户「保留上面那种」「之前卡片
                        本来就有这个功能啊 上传 → 送审 → 通过」），配对产物卡在上方按 carriedProductKeys 收起。 */}
                    {stageRequirements.length > 0 && (
                      <div className="mt-1.5 space-y-1 border-t border-slate-200/70 pt-1.5" data-testid={`dv3-stagechat-requirements-${stageNode.key}`}>
                        <div className="flex items-center gap-1 px-1">
                          <p className="text-[9px] font-bold uppercase tracking-wide text-slate-400">{T("阶段任务所需文件")}</p>
                          {legacy === null && <Loader2 className="h-2.5 w-2.5 animate-spin text-slate-300" />}
                        </div>
                        {stageRequirements.map((r) => {
                          const task = tasksById[r.taskId];
                          const pairedTasks = r.pbDrawings ? r.taskIds.map((id) => tasksById[id]).filter(Boolean) : null;
                          const types = r.pbDrawings ? ["PDF", "CAD"] : (task?.requiredFileTypes?.length ? task.requiredFileTypes : (r.types || []));
                          const files = r.pbDrawings ? pbTaskFiles(pairedTasks) : (task?.files || []);
                          const missing = r.pbDrawings ? pbMissingKinds(pairedTasks) : types.filter((x) => !files.some((f) => f.type === x));
                          const hints = r.pbDrawings ? [] : types.filter((x) => FILE_TYPE_HINT[x]);
                          const canEditTask = !!task && (!r.pbDrawings || pairedTasks.length === 2) && meta?.canEdit;
                          const status = r.pbDrawings ? mergedPbStatus(pairedTasks) : (task?.status || r.status);
                          /* 该行要平铺的全部提交版本（任务侧 + 配对产物侧，见 submissionRowsOf）。
                             「已交 N 份」也改用这份清单长度的口径说明，避免任务侧只有 1 份、
                             产物侧还有 2 份时上面写「已交 1 份」、下面却列出 3 行自相矛盾。 */
                          const rows = submissionRowsByTask[r.taskId] || [];
                          const liveRows = rows.filter((x) => !x.voided);
                          return (
                            <div key={r.taskId} className="rounded border border-slate-200 bg-white px-2 py-1.5" data-testid={`dv3-stagechat-requirement-${stageNode.key}-${r.taskId}`} data-has-task={task ? "1" : "0"}>
                              <div className="flex items-start gap-1">
                                <FileText className="mt-0.5 h-3 w-3 shrink-0 text-slate-400" />
                                <span className="min-w-0 flex-1 text-[10px] font-semibold text-slate-700" title={T(r.title)}>{T(r.title)}</span>
                                {/* 任务状态不再提供手选下拉（2026-10-21 用户需求，与详情阶段卡同一口径）：
                                    「这些可以选择的项都是必传才能进行下一个步骤」—— 状态由文件是否交齐、
                                    产物是否审核通过自动决定（后端 maybe_auto_advance_stage /
                                    design_v3._close_paired_stage_tasks），留只读徽标让人看到进度即可，
                                    避免手点「已完成」绕过必传门禁。 */}
                                <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold ${statusClass(status)}`}
                                  data-testid={`dv3-stagechat-requirement-status-${r.taskId}`}
                                  data-status={status}>{T(STATUS_LABEL[status] || status)}</span>
                              </div>
                              {/* 配对产物的「审核通过 / 退回重做」（2026-10-22 与详情阶段卡对齐）。
                                  配对产物卡已按「一份交付只出现一次」收起，而阶段门禁认的正是
                                  「产物审核通过」—— 审核动作必须在这里可达，否则待审的产物
                                  在会话里没有任何入口（详情把同一组按钮放在任务卡上，
                                  见 LegacyTabs 的 dv3-ltask-pairdisc-review-*）。
                                  权限与后端 _can_review 同口径：主管 / 美国设计师 / 该产物配置的审核人。
                                  只在有待复核记录时出现，避免每行都挂两个按不到的按钮。 */}
                              {(() => {
                                /* 与本行版本清单同一套配对口径（见 submissionRowsByTask）：
                                   标题 → 产物 key → 本阶段的产物对象。 */
                                const keys = r.discKeys || [TASK_TO_DISC[r.title]];
                                const pending = keys.map((key) => stageProducts.find((x) => x.key === key)).filter((disc) =>
                                  disc && (disc.versions || []).some((v) => (v.review || {}).status === "pending"));
                                const canReview = (disc) => meta?.isManager || meta?.me?.role === "us_policy"
                                  || (disc.parties?.reviewer?.username && disc.parties.reviewer.username === meta?.me?.username);
                                const allowed = pending.filter(canReview);
                                if (!allowed.length) return null;
                                const combined = r.pbDrawings && allowed.length === pending.length;
                                const item = allowed[0];
                                const busy = r.pbDrawings ? busyProduct === "pb-drawings" : busyProduct === item.key;
                                return (
                                  <div className="mt-1 flex flex-wrap items-center gap-1 pl-4" data-testid={`dv3-stagechat-requirement-review-${r.taskId}`}>
                                    <span className="text-[9px] font-semibold text-amber-700">{T("待审核")}{r.pbDrawings && !combined ? ` · ${allowed.map((disc) => T(disc.name)).join(" / ")}` : ""}</span>
                                    <button type="button" disabled={busy}
                                      onClick={() => combined ? decidePbDrawings(allowed, "accepted") : decideProduct(item, "accepted")}
                                      data-testid={`dv3-stagechat-requirement-accept-${r.taskId}`}
                                      className="inline-flex items-center gap-0.5 rounded border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50">
                                      <CheckCircle2 className="h-2.5 w-2.5" />{T("审核通过")}
                                    </button>
                                    <button type="button" disabled={busy}
                                      onClick={() => combined ? decidePbDrawings(allowed, "needs_fix") : decideProduct(item, "needs_fix")}
                                      data-testid={`dv3-stagechat-requirement-reject-${r.taskId}`}
                                      className="inline-flex items-center gap-0.5 rounded border border-red-300 bg-red-50 px-1.5 py-0.5 text-[9px] font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50">
                                      <RotateCcw className="h-2.5 w-2.5" />{T("退回")}
                                    </button>
                                  </div>
                                );
                              })()}
                              <p className="mt-0.5 pl-4 text-[9px] text-slate-500">{T("需：{0}", [types.map((x) => TE(x, FILE_TYPE_ZH)).join(" / ")])}</p>
                              {/* 811 Ticket 等只看英文键不知道含义的类型：用一句业务解释补足（用户反馈点名的 811）。 */}
                              {hints.map((x) => (
                                <p key={x} className="mt-0.5 pl-4 text-[9px] leading-4 text-slate-400">{TE(x, FILE_TYPE_ZH)}：{T(FILE_TYPE_HINT[x])}</p>
                              ))}
                              <p className="mt-0.5 pl-4 text-[9px] text-slate-400">
                                {liveRows.length ? T("已交 {0} 份", [liveRows.length]) : T("尚未上传")}
                                {rows.length > liveRows.length ? T("（含作废 {0} 份）", [rows.length - liveRows.length]) : ""}
                                {missing.length ? T(" · 缺 {0}", [missing.map((x) => TE(x, FILE_TYPE_ZH)).join("/")]) : (types.length ? T(" · 已齐") : "")}
                              </p>
                              {/* 提交物逐版平铺（2026-10-21 用户需求）。
                                  用户要求「产物都排列出来」而不是「点按钮才看到 1/2/3 个历史」，
                                  所以这里不再折叠、也不再把详情塞进按钮：每一版独自一行，
                                  带版本号 / 文件名 / 状态 / 提交人（有复核记录时带审核人）/ 时间，
                                  点文件名即在该行清单内开页内灯箱，行尾可直接下载。
                                  作废件仍保留（历史永久留档红线），用删除线弱化并注明原因。 */}
                              {rows.length > 0 && (
                                <div className="mt-1 space-y-0.5 rounded border border-slate-100 bg-slate-50/60 px-1.5 py-1"
                                  data-testid={`dv3-stagechat-requirement-versions-${r.taskId}`} data-count={rows.length}>
                                  {rows.map((row) => (
                                    <div key={`${row.source}-${row.aid || row.version}`}
                                      data-testid={`dv3-stagechat-requirement-version-${r.taskId}-${row.source}-${row.version}`}
                                      data-version={row.version ?? ""} data-source={row.source}
                                      data-voided={row.voided ? "1" : "0"}
                                      data-state={row.state.label}
                                      className={`rounded px-1 py-0.5 text-[9px] leading-4 ${row.voided ? "opacity-60" : ""}`}>
                                      <div className="flex items-center gap-1">
                                        <span className={`shrink-0 rounded px-1 text-[8px] font-bold ${row.voided ? "bg-slate-400 text-white" : "bg-slate-700 text-white"}`}>
                                          {row.version == null ? T("参考") : `v${row.version}`}
                                        </span>
                                        {row.fileId ? (
                                          <button type="button" onClick={() => openSubmissionPreview(r.taskId, row)}
                                            title={T("页内预览：{0}", [row.name])}
                                            data-testid={`dv3-stagechat-requirement-version-open-${r.taskId}-${row.source}-${row.version}`}
                                            className={`min-w-0 flex-1 truncate text-left font-semibold ${row.voided ? "text-slate-400 line-through" : "text-violet-700 hover:underline"}`}>
                                            {row.name}
                                          </button>
                                        ) : (
                                          <span className={`min-w-0 flex-1 truncate font-semibold ${row.voided ? "text-slate-400 line-through" : "text-slate-600"}`}>{row.name}</span>
                                        )}
                                        <span className={`shrink-0 rounded px-1 py-0.5 font-bold ${row.state.cls}`}>{T(row.state.label)}</span>
                                      </div>
                                      <div className="flex flex-wrap items-center gap-x-1 pl-[22px] text-slate-500">
                                        <span>{T("提交")} <b className="font-semibold text-slate-700">{row.who || T("—")}</b></span>
                                        {row.reviewer && (<>
                                          <span className="text-slate-300">·</span>
                                          <span>{T("审核")} <b className="font-semibold text-slate-700">{row.reviewer}</b></span>
                                        </>)}
                                        {/* 类型标签（2026-10-21）：一个任务可能要求多种文件（CAD / 方案PDF），
                                            而版本号是**按类型各自计数**的 —— 不标类型就会出现两个 v1 挨着，
                                            看起来像重复上传。产物侧的版本号属于产物整体，没有类型，故不显示。 */}
                                        {row.type && (<>
                                          <span className="text-slate-300">·</span>
                                          <span className="text-slate-400">{TE(row.type, FILE_TYPE_ZH)}</span>
                                        </>)}
                                        {/* 两侧来源标出来源，避免用户以为「怎么多了一份」：
                                            任务卡上传 / 产物侧上传是两条真实的上传链路，各记各的版本号。 */}
                                        {!row.pbDrawings && (<>
                                          <span className="text-slate-300">·</span>
                                          <span className="text-slate-400">{row.source === "disc" ? T("产物侧") : T("任务卡")}</span>
                                        </>)}
                                      </div>
                                      <div className="flex flex-wrap items-center gap-x-1 pl-[22px] text-slate-400">
                                        <FileUploadDate value={row.uploadedAt} fallbackValue={row.at} />
                                        {row.voided && <span className="text-red-500">{T("· 已作废")}</span>}
                                        {/* 因阶段回退被作废的送审（2026-10-23 用户需求「回退后需要重新审核」）：
                                            这条提示必须出现在**任务要求**这条路径上 —— 用户日常就是从这里
                                            看「这一轮交没交」。只说「因回退作废」不够，要讲清下一步是重新提交，
                                            否则他会以为系统会自己把审核重新发出去。 */}
                                        {row.state.label === "因回退作废" && (
                                          <span className="font-semibold text-rose-700">{T("· 因阶段回退作废，需重新提交送审")}</span>
                                        )}
                                        {row.fileId && (
                                          <button type="button" title={T("下载到本地")}
                                            data-testid={`dv3-stagechat-requirement-version-download-${r.taskId}-${row.source}-${row.version}`}
                                            onClick={() => downloadFile(row.fileId, row.name).catch((e) => toast.error(e?.message || T("下载失败")))}
                                            className="shrink-0 text-slate-400 hover:text-violet-700">
                                            <Download className="inline h-2.5 w-2.5" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {task && (
                                <div className="mt-1 flex flex-wrap items-center gap-1 pl-4">
                                  {canEditTask && (
                                    <label className="inline-flex cursor-pointer items-center gap-0.5 rounded border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[9px] font-semibold text-violet-700 hover:bg-violet-100"
                                      title={T("快速上传文件（按文件名自动识别类型）")}
                                      data-testid={`dv3-stagechat-requirement-upload-${r.taskId}`}>
                                      {uploadingId === task.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <UploadCloud className="h-3 w-3" />}
                                      {T("上传")}
                                      <input type="file" multiple className="hidden" accept={r.pbDrawings ? ".pdf,.dwg,.dxf" : TASK_ACCEPT}
                                        data-testid={`dv3-stagechat-requirement-upload-input-${r.taskId}`}
                                        disabled={uploadingId === task.id}
                                        onChange={(e) => { uploadFiles(task, e.target.files, pairedTasks); e.target.value = ""; }} />
                                    </label>
                                  )}
                                  <button type="button" onClick={() => setFilesTaskId(task.id)}
                                    title={T("文件与版本：预览 / 下载 / 对比 / 上传新版本")}
                                    data-testid={`dv3-stagechat-requirement-files-${r.taskId}`}
                                    className="inline-flex items-center gap-0.5 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[9px] font-semibold text-slate-600 hover:border-violet-300 hover:text-violet-700">
                                    <FolderOpen className="h-3 w-3" />{T("文件与版本")}
                                    {files.length > 0 && <span className="rounded-full bg-violet-600 px-1 text-[8px] text-white">{files.length}</span>}
                                  </button>
                                  {canEditTask && (() => {
                                    // 与详情阶段卡同口径：任务负责人只能从项目负责人里选（2026-10-19 用户需求）。
                                    // 负责人候选来自 v3 详情（含 effectiveManagers），不是 legacy 任务接口。
                                    const ownerOpts = projectOwnerOptions(project, meta?.designers || [], task.primaryOwner);
                                    if (!project) return <Loader2 className="h-3 w-3 animate-spin text-slate-300" />;
                                    return ownerOpts.length ? (
                                      <select value={r.pbDrawings && pairedTasks.some((item) => item.primaryOwner !== task.primaryOwner) ? "" : (task.primaryOwner || "")}
                                        onChange={(e) => r.pbDrawings ? patchPbOwners(pairedTasks, e.target.value) : patchTask(task, { primaryOwner: e.target.value || null })}
                                        title={T("任务负责人（与详情阶段卡一致，只能从项目负责人中选择）")}
                                        className="h-[20px] max-w-[96px] rounded border border-slate-200 bg-white px-1 text-[9px]"
                                        data-testid={`dv3-stagechat-requirement-owner-${r.taskId}`}>
                                        <option value="">{T("未指派")}</option>
                                        {ownerOpts.map((u) => <option key={u.username} value={u.username}>{ownerOptionLabel(u)}</option>)}
                                      </select>
                                    ) : (
                                      <span className="text-[9px] text-amber-600" data-testid={`dv3-stagechat-requirement-owner-empty-${r.taskId}`}>
                                        {T("请先在矩阵设置项目负责人")}
                                      </span>
                                    );
                                  })()}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* City 审图多轮整改（2026-10-22 用户需求）：
                        「city审这个放列表中了吗，详情已经不用以前那个city审的任务那个组件了，
                         阶段会话右侧也该同步改成新的，现在有两个阶段都有这个都需要同步改掉」。

                        修改原因：详情阶段卡早已把旧版「City 状态跟进 (Follow-up)」静态子任务
                        换成了 CityReviewBlock（轮次 / 通过 / 退回 + 图纸与回复函两个必交槽位 +
                        每轮顺延工期），并隐藏了那张旧卡；但阶段会话右侧**一块都没有** ——
                        从矩阵点进 Planning Review / Building Review 的人只看到产物和任务行，
                        既看不到 City 退回了几轮，也没有「这次审过没过」的判定入口，
                        更没法传 City 的结论附件。等于审图阶段最核心的动作在会话里是缺失的。

                        最终效果：与详情**复用同一个 CityReviewBlock 组件**（同一份轮次数据、
                        同一套接口、同一份结论附件校验），放在阶段任务要求行之后、
                        机构对接之前 —— 与详情阶段卡的排布顺序一致：产物 → 任务 →
                        City 审查结果 → 其他机构对接。

                        关键取舍：
                          · 不在这里另写一套只读摘要 —— 详情里已经处理了轮次状态机
                            （待补齐 / 材料交齐待重交 / 已重新提交）、两个必交槽位的上传与作废、
                            结论附件的上传与作废、通过时走 stage_close_core 的门禁，
                            重写必然漂移，而且「通过」必须与详情走同一份关阶段逻辑；
                          · 只在 CITY_REVIEW_STAGES（planning_review / building_review）出现，
                            与详情共用同一份阶段清单常量，两个阶段一起生效；
                          · 旧版「City 状态跟进」子任务不会漏进这一栏 —— 会话的任务行只来自
                            backend _nc_node 的 requiredFiles，而它只收带 requiredFileTypes 的
                            任务，那张旧卡没有必交文件类型（dev 库 416 条实测全为空），
                            所以它既不在这里、也不在任务行里，口径与详情的 visibleTasks 过滤一致。 */}
                    {(() => {
                      /* 【2026-10-22 P+B 合并】合并节点的 key 是 pb_review，不在 CITY_REVIEW_STAGES 里，
                         直接判 stageNode.key 会让整块 City 审查在合并视图里消失。这里取组内
                         第一个审图阶段作为代表 key（planning_review）—— 后端 correction_list
                         会把它解析成整组，返回 merged=true，块内自动变成「一个块覆盖 P+B」。 */
                      const reviewKey = merged
                        ? memberKeys.find((k) => CITY_REVIEW_STAGES.includes(k))
                        : (CITY_REVIEW_STAGES.includes(stageNode.key) ? stageNode.key : null);
                      if (!reviewKey) return null;
                      return (
                        <div className="mt-1.5" data-testid={`dv3-stagechat-cityreview-${stageNode.key}`} data-merged={merged ? "1" : "0"}>
                          <CityReviewBlock pid={pid} stageKey={reviewKey} meta={meta} api={api} onChanged={reloadStage} />
                        </div>
                      );
                    })()}
                    {/* 机构对接（2026-10-22 用户需求「和详情中的阶段对齐」）。
                        详情阶段卡把机构分成两处：挂在产物卡页脚的（如 MEP → 建筑）、
                        以及单独一栏「其他机构对接」（泳池 / 新地址 / 本阶段无对应产物的）。
                        会话右侧原先一处都没有 —— planning_paperwork 有 2 家机构、structural_t24
                        与 building_paperwork 各 2 / 10 家，从矩阵点进来完全看不到，
                        会以为本阶段没有报批动作。这里按详情同一口径补齐：
                        已被本阶段产物承载的机构不重复列（它们在产物卡里），其余列在这里。 */}
                    {(() => {
                      const discKeySet = new Set(stageProducts.map((x) => x.key));
                      const rest = stageAgencies.filter((a) => !(agToDisc[a.type] && discKeySet.has(agToDisc[a.type])));
                      if (!rest.length) return null;
                      /* 【2026-10-22 用户反馈】「这个阶段的其他机构对接是必填吗」「这些东西在流程中
                         不是必选项....他的状态的作用到底是什么?」
                         与详情阶段卡（LegacyTabs 的「其他机构对接」）同一口径拆分：
                         有关联产物但产物挂在别的阶段 → 仍按原样列出（有交付归属）；
                         完全没有对应产物（泳池 / 新地址 / 其他市政）→ 单独一栏「可选项」。
                         为什么必须拆：后端 stage_close_core 全文不读机构表（实测 "agency"
                         出现 0 次），这些机构的完成与否根本不参与阶段关闭，混在主列表里
                         会让右侧的「0/2」被读成「还差 2 项才能往下走」。 */
                      const agLinkedElsewhere = rest.filter((a) => agToDisc[a.type]);
                      const agOptional = rest.filter((a) => !agToDisc[a.type]);
                      const doneOf = (list) => list.filter((a) => AG_DONE.includes(a.status) || a.applicability === "Exempt/Not Applicable").length;
                      const row = (a, optional) => {
                        const dk = agToDisc[a.type];
                        const linked = dk ? stageProducts.find((x) => x.key === dk) : null;
                        const linkedDone = linked && ["approved", "done", "exempt"].includes(linked.status);
                        return (
                          <div key={a.id} className="rounded border border-slate-200 bg-white px-2 py-1.5"
                            data-testid={`dv3-stagechat-agency-${stageNode.key}-${a.id}`}>
                            <AgencyRow a={a} meta={meta} api={api} designers={designers} onChanged={reloadStage} compact docs={agencyDocs[a.type] || []} resp={legacy?.agencyResp || {}} />
                            {/* 选配项不再逐卡重复「无对应产物」：分栏标题已写明整栏语义，
                                每张卡再说一遍只会变成噪音（与详情同一处理）。 */}
                            {!optional && (
                              <p className={`mt-0.5 flex items-start gap-1 text-[9px] ${linked ? (linkedDone ? "text-emerald-700" : "text-amber-700") : "text-slate-400"}`}
                                data-testid={`dv3-stagechat-agency-linked-${a.id}`}>
                                {linked ? (linkedDone ? <CheckCircle2 className="mt-px h-2.5 w-2.5 shrink-0" /> : <Hourglass className="mt-px h-2.5 w-2.5 shrink-0" />) : <span className="h-2.5 w-2.5 shrink-0" />}
                                <span className="min-w-0">{linked
                                  ? <>{T("关联产物：{0} · {1}", [T(linked.name), T(linked.label || STATUS_LABEL[linked.status] || linked.status)])}{linkedDone ? T(" · 图纸已就绪，可送审") : T(" · 图纸未完成，暂不能送审")}</>
                                  : T("无对应产物（选配项）")}</span>
                              </p>
                            )}
                          </div>
                        );
                      };
                      return (
                        <div className="mt-1.5 space-y-1 border-t border-slate-200/70 pt-1.5" data-testid={`dv3-stagechat-agencies-${stageNode.key}`}>
                          <div className="flex items-center gap-1 px-1">
                            <Landmark className="h-2.5 w-2.5 shrink-0 text-slate-400" />
                            <p className="min-w-0 flex-1 text-[9px] font-bold uppercase tracking-wide text-slate-400">{T("其他机构对接")}</p>
                            <span className="shrink-0 text-[9px] font-semibold tabular-nums text-slate-500" data-testid={`dv3-stagechat-agencies-count-${stageNode.key}`}>
                              {doneOf(rest)}/{rest.length}
                            </span>
                            <span className="shrink-0 text-[9px] text-slate-400">{T("不参与阶段推进")}</span>
                          </div>
                          {agLinkedElsewhere.length > 0 && <div className="space-y-1">{agLinkedElsewhere.map((a) => row(a, false))}</div>}
                          {agOptional.length > 0 && (
                            <div className="space-y-1" data-testid={`dv3-stagechat-agencies-optional-${stageNode.key}`}>
                              <div className="flex items-center gap-1 px-1">
                                <span className="h-2.5 w-0.5 rounded-full bg-slate-300 shrink-0" />
                                <p className="shrink-0 text-[9px] font-bold uppercase tracking-wide text-slate-500">{T("可选项")}</p>
                                <span className="shrink-0 text-[9px] font-semibold tabular-nums text-slate-400">{doneOf(agOptional)}/{agOptional.length}</span>
                                <span className="min-w-0 truncate text-[9px] text-slate-400">{T("无对应产物 · 本单不需要时可不处理")}</span>
                              </div>
                              {agOptional.map((a) => row(a, true))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {/* Land Survey（土地测绘）可选块 —— 2026-10-22 用户需求：
                        「然后就阶段会话右侧在方案设计里没有显示 这个可选的土地测绘」
                        「注意土地测绘是可选项哦..参考详情」。

                        修改原因：这块此前只渲染在项目详情（LegacyTabs 的任务网格里），
                        阶段会话右侧完全没有 —— 从矩阵点进「方案设计」会话的人看不到它，
                        以为这个阶段只有 Floor Plan 一项交付。

                        最终效果：与详情**复用同一个 LandSurveyBlock 组件**（同一份状态、
                        同一套接口、同一个「可选」语义），放在产物 / 任务 / 机构之后，
                        因为它是本阶段的可选补充项，不是阶段门禁的一部分。

                        关键取舍：
                          · 不在这里另写一套简化 UI —— 详情里已经处理了三种状态
                            （未登记 / 等报告 / 已完成）、跟进待办、报告上传与作废，
                            重写必然漂移；
                          · 只在 LAND_SURVEY_STAGES（当前 = 方案设计 schematic）里出现，
                            与详情共用同一份阶段清单常量，不会两处不一致；
                          · 它不参与阶段门禁（stage_close_core 不读测绘），所以不加在
                            productGate 的计数里，避免被读成「还差这一项才能完成阶段」。 */}
                    {LAND_SURVEY_STAGES.includes(stageNode.key) && (
                      <div className="mt-1.5 space-y-1 border-t border-slate-200/70 pt-1.5" data-testid={`dv3-stagechat-landsurvey-${stageNode.key}`}>
                        <div className="flex items-center gap-1 px-1">
                          <MapIcon className="h-2.5 w-2.5 shrink-0 text-sky-600" />
                          <p className="min-w-0 flex-1 text-[9px] font-bold uppercase tracking-wide text-slate-400">{T("Land Survey（土地测绘）")}</p>
                          <span className="shrink-0 rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-bold text-slate-500">{T("可选")}</span>
                        </div>
                        <LandSurveyBlock pid={pid} stageKey={stageNode.key} meta={meta} api={api} onChanged={reloadStage} />
                      </div>
                    )}
                  </div>
                </section>
              </div>
            )}
          </aside>
        </div>
      </div>
      {/* 批量操作弹窗（2026-10-19 用户需求）：
          打开时默认全部勾选；取消勾选的产物 = 本阶段无需提交，不影响流程推进。
          保存后这些产物从右侧主列表收进「无需提交」折叠区，可随时恢复。
          说明：不删除任何数据，也不改全局「产物挂哪个阶段」的配置，只作用当前项目。 */}
      {batchOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/40 p-4" data-testid="dv3-stagechat-batch-dialog">
          <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-4 shadow-xl">
            <div className="flex items-center gap-2">
              <Settings2 className="h-4 w-4 text-violet-600" />
              <p className="flex-1 text-sm font-bold text-slate-800">{T("批量设置本阶段产物")}</p>
              <button type="button" onClick={() => setBatchOpen(false)} className="text-slate-400 hover:text-slate-800" data-testid="dv3-stagechat-batch-close"><X className="h-4 w-4" /></button>
            </div>
            <p className="mt-1 text-[11px] leading-5 text-slate-500">
              {T("勾选 = 需要提交；取消勾选 = 本单无需提交，不影响阶段推进。取消的项目会收进「无需提交」折叠区，可随时恢复。")}
            </p>
            <div className="mt-2 max-h-[46vh] space-y-1 overflow-y-auto">
              {batchScopeProducts.map((item) => {
                const on = draftKeep.includes(item.key);
                return (
                  <label key={item.key} className={`flex cursor-pointer items-center gap-2 rounded-md border px-2 py-1.5 ${on ? "border-violet-200 bg-violet-50/50" : "border-slate-200 bg-slate-50"}`}
                    data-testid={`dv3-stagechat-batch-row-${item.key}`}>
                    <input type="checkbox" checked={on} onChange={() => toggleDraft(item.key)}
                      data-testid={`dv3-stagechat-batch-check-${item.key}`} className="h-4 w-4 accent-violet-600" />
                    <Package className={`h-3.5 w-3.5 shrink-0 ${on ? "text-violet-500" : "text-slate-400"}`} />
                    <span className={`min-w-0 flex-1 truncate text-[11px] font-semibold ${on ? "text-slate-800" : "text-slate-500"}`}>{T(item.name)}</span>
                    {item.skipped && <span className="shrink-0 rounded-full bg-slate-200 px-1.5 py-0.5 text-[9px] font-bold text-slate-600">{T("当前无需提交")}</span>}
                    <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold ${statusClass(item.status)}`}>{T(item.label || STATUS_LABEL[item.status] || item.status)}</span>
                  </label>
                );
              })}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button type="button" onClick={() => setDraftKeep(batchScopeProducts.map((x) => x.key))}
                className="text-[11px] font-semibold text-violet-700 hover:underline" data-testid="dv3-stagechat-batch-all">{T("全选")}</button>
              <button type="button" onClick={() => setDraftKeep([])}
                className="text-[11px] font-semibold text-slate-500 hover:underline" data-testid="dv3-stagechat-batch-none">{T("全不选")}</button>
              <span className="ml-auto text-[10px] text-slate-400" data-testid="dv3-stagechat-batch-count">
                {T("需提交 {0} / {1}", [draftKeep.length, batchScopeProducts.length])}
              </span>
            </div>
            <div className="mt-3 flex justify-end gap-2">
              <button type="button" onClick={() => setBatchOpen(false)}
                className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50" data-testid="dv3-stagechat-batch-cancel">{T("取消")}</button>
              <button type="button" onClick={saveBatch} disabled={savingSkip}
                className="inline-flex items-center gap-1 rounded-md bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700 disabled:opacity-60" data-testid="dv3-stagechat-batch-save">
                {savingSkip && <Loader2 className="h-3.5 w-3.5 animate-spin" />}{T("保存")}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* 文件与版本弹窗与详情阶段卡共用；z-index 在阶段抽屉内部，只盖住抽屉内容。
          这里传最新的 tasksById 对象，上传 / 改状态后不会显示旧的版本列表。 */}
      {filesTask && (
        <TaskFilesModal task={filesTask} api={api} zh isMgr={meta?.isManager} canEdit={!!meta?.canEdit}
          pairedTasks={pairedFilesTasks?.length === 2 ? pairedFilesTasks : null}
          onClose={() => setFilesTaskId(null)} onChanged={reloadStage} />
      )}
      {/* 版本附件页内预览（2026-10-20）：替代原来的 target=_blank 下载链接，
          避免 zip / CAD / 大 PDF 打开空白标签页。PDF 走 ProjectPdfPreview 逐页渲染。 */}
      {previewVersion && previewItems.length > 0 && (
        <MediaLightbox tid="dv3-stagechat-version-lightbox" items={previewItems}
          index={Math.min(previewVersion.index, previewItems.length - 1)}
          onIndex={(i) => setPreviewVersion((v) => (v ? { ...v, index: i } : v))}
          onClose={() => setPreviewVersion(null)} api={api} zh />
      )}
      {/* 任务行提交物灯箱（2026-10-21 多版本平铺）：与该行屏幕上看到的版本顺序一致，
          上一张 / 下一张就是在同一行的各版之间翻。 */}
      {previewSubmission && previewSubmissionItems.length > 0 && (
        <MediaLightbox tid="dv3-stagechat-submission-lightbox" items={previewSubmissionItems}
          index={Math.min(previewSubmission.index, previewSubmissionItems.length - 1)}
          onIndex={(i) => setPreviewSubmission((v) => (v ? { ...v, index: i } : v))}
          onClose={() => setPreviewSubmission(null)} api={api} zh />
      )}
    </div>,
    document.body
  );
};

export default StageChatDrawer;
